import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import {
  DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors, DragOverlay,
} from "@dnd-kit/core";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, useLookup } from "@/components/shared";
import ProjectDetailsDialog from "@/components/ProjectDetailsDialog";
import DashboardView from "@/components/DashboardView";
import WeeklySummaryView from "@/components/WeeklySummaryView";
import { fmtDate, fmtDateTime } from "@/lib/date";
import DocketDetailModal from "@/components/DocketDetailModal";
import SiteDiaryModal from "@/components/SiteDiaryModal";
import OrganiseTransportModal from "@/components/OrganiseTransportModal";
import TransportDetailModal from "@/components/TransportDetailModal";
import { complianceStatus } from "@/components/ComplianceTab";
import { SkeletonRows } from "@/components/RegisterSkeleton";
import {
  Plus, ArrowsClockwise, MagnifyingGlass, Truck, HardHat, DotsThreeVertical,
  Rows, CalendarBlank, MapPin, DotsSixVertical, Trash, PencilSimple, X, Columns, CaretDown, CaretLeft, CaretRight, ShareNetwork, ChartBar, FileText, Car, ArrowsInLineVertical, ArrowsOutLineVertical, UsersThree,
} from "@phosphor-icons/react";

const fmt = (d) => fmtDate(d);

/* ------------------------- Draggable resource card ------------------------ */
const SelectionCtx = React.createContext({ selectedIds: [], selectedKind: null, onCardClick: () => {} });
const RowDrawerCtx = React.createContext({ open: () => {} });

const CARD_STATUS = {
  on_hire: { c: "bg-equipr-yellow", t: "On hire" },
  off_hire: { c: "bg-gray-400", t: "Off hire" },
  stand_down: { c: "bg-red-500", t: "Stand down" },
  maintenance: { c: "bg-orange-400", t: "Maintenance" },
};

// Stand-down is date-bound: only show it while today is within the stand-down window.
function effStatus(a) {
  if (a && a.status === "stand_down") {
    const t = new Date().toISOString().slice(0, 10);
    if ((a.stand_down_from && t < a.stand_down_from) || (a.stand_down_to && t > a.stand_down_to)) return "active";
  }
  return a ? a.status : undefined;
}


function ResourceCard({ item, kind, orderedIds }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${kind}:${item.id}`, data: { kind, item },
  });
  const sel = React.useContext(SelectionCtx);
  const selected = sel.selectedKind === kind && sel.selectedIds.includes(item.id);
  const allocated = item.status && item.status !== "available";
  const st = CARD_STATUS[item.status];
  return (
    <div ref={setNodeRef} {...listeners} {...attributes}
      onClick={(e) => sel.onCardClick(item, kind, e, orderedIds)}
      data-testid={`resource-${item.id}`}
      aria-selected={selected}
      className={`flex items-center gap-2 px-2.5 py-2 border-b border-divider-row cursor-grab active:cursor-grabbing ${selected ? "bg-accent-tint ring-1 ring-inset ring-accent-edge" : "bg-white hover:bg-accent-wash"} ${isDragging ? "opacity-40" : ""} ${allocated ? "opacity-90" : ""}`}>
      <DotsSixVertical size={14} className="text-ink-disabled shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-ink-primary truncate flex items-center gap-1">
          {kind === "asset" ? item.asset_no : kind === "vehicle" ? item.rego : item.name}
          {item._in_transit && (
            <span title="In transit" data-testid={`resource-intransit-${item.id}`}
              className="inline-flex items-center gap-1 bg-[#EFF6FF] text-[#1D4ED8] border border-[#BFD7FE] text-[10px] font-bold uppercase tracking-[0.03em] px-1.5 py-0.5 rounded-[5px]">
              <Truck size={10} weight="bold" /> In Transit
            </span>
          )}
        </div>
        {kind === "operator" ? (
          (item.skills || []).length ? (
            <div className="flex flex-wrap gap-1 mt-0.5" data-testid={`resource-skills-${item.id}`}>
              {item.skills.map((s) => (
                <span key={s} className="inline-block bg-surface-inset border border-border text-ink-secondary text-[10px] font-semibold px-1.5 py-px leading-tight rounded-[4px]">{s}</span>
              ))}
            </div>
          ) : <div className="text-[11px] text-ink-tertiary">Worker</div>
        ) : (
          <div className="text-[11px] text-ink-tertiary truncate">{kind === "asset" ? `${item.asset_type || ""}${item.is_auxiliary ? " · Attachment" : ""}` : ([item.make, item.model].filter(Boolean).join(" ") || "Vehicle")}</div>
        )}
      </div>
      {allocated && st && (
        <span title={st.t} data-testid={`resource-status-${item.id}`}
          className={`shrink-0 inline-block w-2 h-2 rounded-full ${st.c}`} />
      )}
    </div>
  );
}

/* ---------------------------- Right rail panel ---------------------------- */
function CrewCard({ crew }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `crew-${crew.id}`, data: { kind: "crew", item: crew },
  });
  const n = (crew.member_ids || []).length;
  return (
    <div ref={setNodeRef} {...listeners} {...attributes} data-testid={`crew-card-${crew.id}`}
      className={`px-3 py-2 border-b border-gray-100 cursor-grab select-none bg-equipr-yellow/10 hover:bg-equipr-yellow/25 ${isDragging ? "opacity-40" : ""}`}>
      <div className="flex items-center gap-1.5 text-[13px] font-bold text-equipr-black">
        <UsersThree size={15} weight="bold" className="text-accent-ink" /> {crew.name}
      </div>
      <div className="text-[11px] text-gray-500">{n} worker{n === 1 ? "" : "s"} — drag to allocate all</div>
    </div>
  );
}

function RightRail({ assets, operators, vehicles, crews, onClose, onRefresh }) {
  const { user } = useAuth();
  const sel = React.useContext(SelectionCtx);
  const scope = user?.role === "admin" ? "both" : (user?.resource_scope || "both");
  const TAB_DEFS = { assets: ["assets", "Assets", Truck], operators: ["operators", "Workers", HardHat], vehicles: ["vehicles", "Vehicles", Car] };
  const tabDefs = scope === "assets" ? [TAB_DEFS.assets, TAB_DEFS.vehicles]
    : scope === "workers" ? [TAB_DEFS.operators]
    : [TAB_DEFS.assets, TAB_DEFS.operators, TAB_DEFS.vehicles];
  const [tab, setTab] = useState(scope === "workers" ? "operators" : "assets");
  const [spinning, setSpinning] = useState(false);
  const doRefresh = () => { if (onRefresh) { setSpinning(true); onRefresh(); setTimeout(() => setSpinning(false), 700); } };
  const [q, setQ] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [showAllocated, setShowAllocated] = useState(false);
  const list = tab === "assets" ? assets : tab === "vehicles" ? (vehicles || []) : operators;

  const supplierOptions = useMemo(
    () => Array.from(new Set(list.map((x) => x.supplier).filter(Boolean))).sort(),
    [list]
  );

  const filtered = useMemo(() => list.filter((x) => {
    if (!showAllocated && x.status !== "available") return false;
    if (supplierFilter && (x.supplier || "") !== supplierFilter) return false;
    if (q && !JSON.stringify(x).toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [list, showAllocated, supplierFilter, q]);

  const availCount = list.filter((x) => x.status === "available").length;

  const grouped = useMemo(() => {
    const g = {};
    const AUX = "Attachments";
    filtered.forEach((x) => {
      const s = (tab === "assets" && x.is_auxiliary) ? AUX : (x.supplier || "Unassigned");
      (g[s] = g[s] || []).push(x);
    });
    const out = {};
    Object.keys(g).sort((a, b) => (a === AUX) - (b === AUX) || a.localeCompare(b)).forEach((k) => { out[k] = g[k]; });
    return out;
  }, [filtered, tab]);

  const orderedIds = useMemo(() => Object.values(grouped).flat().map((x) => x.id), [grouped]);
  const currentKind = tab === "assets" ? "asset" : tab === "vehicles" ? "vehicle" : "operator";
  const selCount = sel.selectedKind === currentKind ? sel.selectedIds.length : 0;

  return (
    <div className="w-72 bg-surface-card border-l border-border flex flex-col shrink-0 no-print">
      <div className="flex items-center justify-between gap-2 px-2 py-2 border-b border-border">
        <div className="flex items-center bg-surface-inset rounded-[999px] p-0.5">
          {tabDefs.map(([k, l]) => (
            <button key={k} onClick={() => { setTab(k); sel.clear(); }} data-testid={`rail-tab-${k}`}
              className={`px-2.5 h-8 rounded-[999px] text-[11.5px] font-bold uppercase tracking-[0.04em] transition-colors ${tab === k ? "bg-equipr-yellow text-black" : "text-ink-secondary hover:text-ink-primary"}`}>
              {l}
            </button>
          ))}
        </div>
        {(onRefresh || onClose) && (
          <div className="flex items-center gap-1 shrink-0">
            {onRefresh && (
              <button onClick={doRefresh} data-testid="rail-refresh" title="Refresh list" className="w-8 h-8 flex items-center justify-center rounded-[7px] text-ink-tertiary hover:text-ink-primary hover:bg-surface-panel">
                <ArrowsClockwise size={16} weight="bold" className={spinning ? "animate-spin" : ""} />
              </button>
            )}
            {onClose && (
              <button onClick={onClose} data-testid="rail-close" title="Hide resources" className="w-8 h-8 flex items-center justify-center rounded-[7px] text-ink-tertiary hover:text-ink-primary hover:bg-surface-panel">
                <X size={16} weight="bold" />
              </button>
            )}
          </div>
        )}
      </div>
      <div className="p-2 border-b border-divider-inner space-y-2">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Unit #, type or supplier" data-testid="rail-search"
            className="w-full h-9 pl-8 pr-2 border border-border-control bg-white text-[13px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none" />
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-[12px] text-ink-secondary cursor-pointer">
            <input type="checkbox" checked={showAllocated} onChange={(e) => setShowAllocated(e.target.checked)} data-testid="rail-show-allocated" />
            Show allocated
          </label>
          <span className="text-[11px] text-ink-tertiary" data-testid="rail-count">{filtered.length} shown · {availCount} free</span>
        </div>
        {selCount > 1 ? (
          <div className="flex items-center justify-between bg-accent-tint border border-accent-edge rounded-[6px] px-2 py-1" data-testid="rail-selection-bar">
            <span className="text-[12px] font-semibold text-ink-primary" data-testid="rail-selection-count">{selCount} selected — drag any to a project</span>
            <button onClick={sel.clear} data-testid="rail-selection-clear" className="text-[11px] font-bold text-ink-secondary hover:text-ink-primary underline">Clear</button>
          </div>
        ) : (
          <p className="text-[10.5px] text-ink-tertiary">Ctrl/Cmd-click or Shift-click to select several, then drag.</p>
        )}
      </div>
      <div className="flex-1 overflow-y-auto">
        {tab === "operators" && (crews || []).length > 0 && (
          <div data-testid="rail-crews-section">
            <div className="bg-[#121212] px-2 py-1.5 text-[9.5px] font-bold uppercase tracking-[0.16em] text-equipr-yellow sticky top-0 z-10">Crews</div>
            {crews.map((c) => <CrewCard key={c.id} crew={c} />)}
          </div>
        )}
        {Object.keys(grouped).length === 0 && (
          <div className="p-4 text-center text-[13px] text-ink-tertiary" data-testid="rail-empty">
            {showAllocated ? "Nothing matches." : "None available — tick 'Show allocated' to schedule ahead."}
          </div>
        )}
        {Object.entries(grouped).map(([supplier, items]) => (
          <div key={supplier}>
            <div className="bg-surface-sunken px-2.5 py-1 text-[9.5px] font-bold uppercase tracking-[0.14em] text-ink-muted sticky top-0 border-b border-divider-row">{supplier}</div>
            {items.map((it) => <ResourceCard key={it.id} item={it} kind={currentKind} orderedIds={orderedIds} />)}
          </div>
        ))}
      </div>
    </div>
  );
}

/* --------------------- Column catalog & saved views ----------------------- */
const fmtRate = (r, t) => (r != null && r !== "" ? `$${r}${t ? " / " + t : ""}` : "—");

const COLUMN_CATALOG = {
  label: { label: "Asset / Worker", w: 3, locked: true,
    render: (a) => {
      const tag = a.kind === "operator" ? "WK" : a.kind === "vehicle" ? "VE" : "PL";
      return (
        <span className="inline-flex items-center gap-2 min-w-0">
          <span data-testid={`kind-tag-${a.id}`} className="shrink-0 font-mono text-[9px] font-bold tracking-wide w-6 h-[18px] flex items-center justify-center rounded-[4px] bg-surface-inset border border-border text-ink-secondary">{tag}</span>
          <span className="font-semibold text-ink-primary truncate">{a.label}</span>
          {a.kind === "vehicle" && a.driver_name ? <span className="text-ink-tertiary font-normal truncate" data-testid={`vehicle-driver-${a.id}`}>· {a.driver_name}</span> : null}
        </span>
      );
    } },
  asset_type: { label: "Type", w: 2, render: (a) => a.asset_type || (a.kind === "operator" ? "Worker" : a.kind === "vehicle" ? "Vehicle" : "—") },
  hire_type: { label: "Hire Type", w: 2, edit: { type: "lookup", lookup: "hire_types" }, render: (a) => a.hire_type || "—" },
  supplier: { label: "Supplier", w: 2.6, render: (a) => a.supplier || "—" },
  zone: { label: "Site Zone", w: 1.8, edit: { type: "text" }, render: (a) => a.zone || "—" },
  start_date: { label: "Start", w: 1.4, edit: { type: "date" }, render: (a) => fmt(a.start_date) },
  end_date: { label: "End", w: 1.4, edit: { type: "date" }, render: (a) => fmt(a.end_date) },
  status: { label: "Status", w: 1.5, render: (a) => { const s = effStatus(a); return <StatusBadge value={a.kind === "operator" ? (s === "active" ? "mobilised" : s) : (s === "active" ? "on_hire" : s)} />; } },
  sup_rate: { label: "Sup. Rate", w: 1.4, edit: { type: "number" }, render: (a) => fmtRate(a.sup_rate, a.sup_rate_type) },
  sup_rate_type: { label: "Sup. Rate Type", w: 1.6, edit: { type: "lookup", lookup: "rate_types" }, render: (a) => a.sup_rate_type || "—" },
  cust_rate: { label: "Cust. Rate", w: 1.4, render: (a) => fmtRate(a.cust_rate, a.cust_rate_type) },
  cust_rate_type: { label: "Cust. Rate Type", w: 1.6, render: (a) => a.cust_rate_type || "—" },
  notes: { label: "Notes", w: 2.6, edit: { type: "text" }, render: (a) => a.notes || "—" },
};
const ALL_COLUMN_KEYS = Object.keys(COLUMN_CATALOG);
const DEFAULT_COLUMNS = ["label", "asset_type", "hire_type", "supplier", "start_date", "end_date", "status"];
const orderCols = (keys) => ALL_COLUMN_KEYS.filter((k) => k === "label" || keys.includes(k));
const ACTIONS_WEIGHT = 0.5;

function ColGroup({ columns }) {
  const total = columns.reduce((s, k) => s + COLUMN_CATALOG[k].w, 0) + ACTIONS_WEIGHT;
  const pct = (w) => `${(w / total) * 100}%`;
  return (
    <colgroup>
      {columns.map((k) => <col key={k} style={{ width: pct(COLUMN_CATALOG[k].w) }} />)}
      <col style={{ width: pct(ACTIONS_WEIGHT) }} />
    </colgroup>
  );
}

const COMBO_COLS = ["asset_type", "hire_type", "status"];

function ListHeader({ columns, sort, onSort }) {
  const caret = (k) => sort.key === k ? (sort.dir === "asc" ? " ▲" : " ▼") : "";
  return (
    <div className="sticky top-0 z-20 bg-surface-sunken shadow-[0_1px_0_#DAD5CA]">
      <table className="w-full text-[12px] table-fixed">
        <ColGroup columns={columns} />
        <thead>
          <tr className="bg-surface-sunken border-b border-border">
            {columns.map((k, i) => (
              <th key={k} onClick={() => onSort(k)} data-testid={`sort-${k}`}
                className={`px-3 py-2.5 uppercase tracking-[0.1em] text-colhead text-left truncate cursor-pointer select-none hover:text-ink-primary ${i === 0 ? "font-bold text-ink-secondary" : "font-medium text-ink-muted"}`}>
                {COLUMN_CATALOG[k].label}<span className="text-accent-ink">{caret(k)}</span>
              </th>
            ))}
            <th className="px-3 py-2.5"></th>
          </tr>
        </thead>
      </table>
    </div>
  );
}

// value used for filtering & sorting a given column
function cellValue(a, key) {
  if (key === "label") return a.label || "";
  if (key === "asset_type") return a.asset_type || (a.kind === "operator" ? "Worker" : "");
  if (key === "status") return a.status === "active" ? "on hire" : (a.status || "").replace("_", " ");
  const v = a[key];
  return v == null ? "" : String(v);
}

function applyFilterSort(assignments, query, sort) {
  let rows = assignments;
  const q = (query || "").trim().toLowerCase();
  if (q) {
    const keys = ["label", "asset_type", "hire_type", "supplier", "status", "zone", "notes"];
    rows = rows.filter((a) => keys.some((k) => cellValue(a, k).toLowerCase().includes(q)));
  }
  if (sort.key) {
    const numeric = ["sup_rate", "cust_rate"].includes(sort.key);
    const dated = ["start_date", "end_date"].includes(sort.key);
    rows = [...rows].sort((x, y) => {
      let vx = cellValue(x, sort.key), vy = cellValue(y, sort.key);
      if (numeric) { vx = parseFloat(vx) || 0; vy = parseFloat(vy) || 0; }
      else if (dated) { vx = vx ? new Date(vx).getTime() : 0; vy = vy ? new Date(vy).getTime() : 0; }
      else { vx = vx.toLowerCase(); vy = vy.toLowerCase(); }
      if (vx < vy) return sort.dir === "asc" ? -1 : 1;
      if (vx > vy) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }
  return rows;
}

/* ---------------------------- Action modal -------------------------------- */
const ACTION_META = {
  edit_details: { title: "Edit Line Details", color: "primary" },
  off_hire: { title: "Off Hire", color: "danger" },
  demobilise: { title: "Demobilise Worker", color: "danger" },
  stand_down: { title: "Stand Down", color: "danger" },
};

function ActionModal({ action, assignment, projectId, onClose, onDone }) {
  const standReasons = useLookup("standdown_reasons");
  const offReasons = useLookup("offhire_reasons");
  const rateTypes = useLookup("rate_types");
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    reason: "",
    off_hire_date: today,
    stand_down_from: assignment.start_date || today,
    stand_down_to: assignment.end_date || today,
    start_date: assignment.start_date || today,
    end_date: assignment.end_date || today,
    sup_rate_type: assignment.sup_rate_type || "",
    sup_rate: assignment.sup_rate ?? "",
    cust_rate_type: assignment.cust_rate_type || "",
    cust_rate: assignment.cust_rate ?? "",
    notes: assignment.notes || "",
    arrange_transport: false,
    transport_to_type: "site",
    transport_to_id: "",
    transport_date: today,
    driver_id: assignment.driver_id || "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [dests, setDests] = useState({ projects: [], sites: [] });
  const [workers, setWorkers] = useState([]);
  useEffect(() => {
    if (action !== "edit_details" || assignment.kind !== "vehicle") return;
    Promise.all([api.get("/operators"), api.get(`/projects/${projectId}`)]).then(([o, p]) => {
      const onProject = new Set((p.data.assignments || []).filter((x) => x.kind === "operator" && x.status !== "off_hire" && x.status !== "demobilised").map((x) => x.ref_id));
      const list = o.data.filter((w) => w.employment_status !== "inactive").map((w) => ({ id: w.id, name: w.name, onProject: onProject.has(w.id) }));
      list.sort((x, y) => (y.onProject - x.onProject) || x.name.localeCompare(y.name));
      setWorkers(list);
    }).catch(() => {});
  }, [action, assignment.kind, projectId]);
  useEffect(() => {
    if (action !== "off_hire" || assignment.kind !== "asset") return;
    Promise.all([api.get("/projects"), api.get("/sites")]).then(([p, s]) => {
      setDests({
        projects: p.data.map((x) => ({ id: x.id, name: x.project_name || x.site_address || x.project_no || "Project" })),
        sites: s.data.map((x) => ({ id: x.id, name: `${x.name}${x.site_type ? ` (${x.site_type})` : ""}` })),
      });
    }).catch(() => {});
  }, [action, assignment.kind]);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const inp = "w-full h-9 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none";
  const lbl = "text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted mb-1.5 block";

  const submit = async () => {
    setError(""); setSaving(true);
    try {
      if (action === "off_hire") {
        if (!form.reason) throw { response: { data: { detail: "Please choose a reason." } } };
        await api.post("/offhires", { asset_no: assignment.label, supplier: assignment.supplier, project_id: projectId, reason: form.reason, off_hire_date: form.off_hire_date });
        await api.put(`/projects/${projectId}/assign/${assignment.id}`, { status: "off_hire", end_date: form.off_hire_date });
        if (form.arrange_transport && assignment.kind === "asset") {
          if (!form.transport_to_id) throw { response: { data: { detail: "Please choose a transport destination." } } };
          const pool = form.transport_to_type === "project" ? dests.projects : dests.sites;
          const dest = pool.find((d) => d.id === form.transport_to_id);
          await api.post("/transportation", {
            asset_no: assignment.label, project_id: projectId,
            from_type: "project", from_id: projectId, from_location: "Current project",
            to_type: form.transport_to_type, to_id: form.transport_to_id, to_location: dest?.name || "",
            scheduled_at: form.transport_date, status: "scheduled",
            notes: `Off-hire transport — ${form.reason}`,
          });
        }
      } else if (action === "demobilise") {
        if (form.end_date && form.start_date && form.end_date < form.start_date)
          throw { response: { data: { detail: "Demobilise date cannot be before the start date." } } };
        await api.put(`/projects/${projectId}/assign/${assignment.id}`, { status: "demobilised", end_date: form.off_hire_date, notes: form.notes });
      } else if (action === "stand_down") {
        if (!form.reason) throw { response: { data: { detail: "Please choose a reason." } } };
        await api.post("/standdowns", { asset_no: assignment.label, supplier: assignment.supplier, project_id: projectId, reason: form.reason, stand_down_from: form.stand_down_from, stand_down_to: form.stand_down_to });
        await api.put(`/projects/${projectId}/assign/${assignment.id}`, { status: "stand_down", stand_down_from: form.stand_down_from, stand_down_to: form.stand_down_to });
      } else if (action === "edit_details") {
        if (form.end_date && form.start_date && form.end_date < form.start_date)
          throw { response: { data: { detail: "End Date cannot be before Start Date." } } };
        await api.put(`/projects/${projectId}/assign/${assignment.id}`, {
          start_date: form.start_date,
          end_date: form.end_date,
          sup_rate_type: form.sup_rate_type,
          sup_rate: form.sup_rate === "" ? null : Number(form.sup_rate),
          cust_rate_type: form.cust_rate_type,
          cust_rate: form.cust_rate === "" ? null : Number(form.cust_rate),
          notes: form.notes,
          ...(assignment.kind === "vehicle" ? { driver_id: form.driver_id } : {}),
        });
      }
      onDone();
      onClose();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong.");
    }
    setSaving(false);
  };

  const meta = ACTION_META[action];

  return createPortal(
    <div className="fixed inset-0 z-50 bg-[rgba(26,26,26,0.34)] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface-panel border border-border-warm rounded-[14px] overflow-hidden shadow-modal w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="action-modal">
        <div className="flex items-center justify-between px-5 py-3 bg-equipr-black text-white">
          <h3 className="font-bold text-sm">{meta.title}</h3>
          <button onClick={onClose} data-testid="action-modal-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-gray-500">
            {assignment.kind === "operator" ? "Worker" : assignment.kind === "vehicle" ? "Vehicle" : "Asset"}: <span className="font-semibold text-equipr-black">{assignment.label}</span>
            {assignment.supplier ? ` · ${assignment.supplier}` : ""}
          </div>
          {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="action-modal-error">{error}</div>}

          {action === "off_hire" && (
            <>
              <div><label className={lbl}>Reason *</label>
                <select className={inp} value={form.reason} onChange={(e) => set("reason", e.target.value)} data-testid="action-reason">
                  <option value="">— Select reason —</option>{offReasons.map((r) => <option key={r}>{r}</option>)}
                </select></div>
              <div><label className={lbl}>Off Hire Date</label>
                <input type="date" className={inp} value={form.off_hire_date} onChange={(e) => set("off_hire_date", e.target.value)} data-testid="action-offhire-date" /></div>
              {assignment.kind === "asset" && (
                <div className="border-t border-gray-200 pt-3 mt-1">
                  <label className="flex items-center gap-2 text-sm font-semibold text-equipr-black cursor-pointer">
                    <input type="checkbox" checked={form.arrange_transport} onChange={(e) => set("arrange_transport", e.target.checked)} data-testid="action-arrange-transport" className="w-4 h-4 accent-equipr-yellow" />
                    Arrange transport off site
                  </label>
                  {form.arrange_transport && (
                    <div className="mt-3 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div><label className={lbl}>Destination Type</label>
                          <select className={inp} value={form.transport_to_type} onChange={(e) => { set("transport_to_type", e.target.value); set("transport_to_id", ""); }} data-testid="action-transport-type">
                            <option value="site">Site (depot/hangar)</option>
                            <option value="project">Project</option>
                          </select></div>
                        <div><label className={lbl}>Transport Date</label>
                          <input type="date" className={inp} value={form.transport_date} onChange={(e) => set("transport_date", e.target.value)} data-testid="action-transport-date" /></div>
                      </div>
                      <div><label className={lbl}>Destination *</label>
                        <select className={inp} value={form.transport_to_id} onChange={(e) => set("transport_to_id", e.target.value)} data-testid="action-transport-dest">
                          <option value="">— Select destination —</option>
                          {(form.transport_to_type === "project" ? dests.projects : dests.sites).map((d) => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                          ))}
                        </select></div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {action === "demobilise" && (
            <>
              <div><label className={lbl}>Demobilise date</label>
                <input type="date" className={inp} value={form.off_hire_date} onChange={(e) => set("off_hire_date", e.target.value)} data-testid="action-demob-date" /></div>
              <div><label className={lbl}>Note (optional)</label>
                <input className={inp} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="e.g. end of assignment, moved to another crew" data-testid="action-demob-note" /></div>
              <p className="text-[12px] text-ink-tertiary">Releases this worker from the project. They stay on the register and can be re-assigned any time.</p>
            </>
          )}

          {action === "stand_down" && (
            <>
              <div><label className={lbl}>Reason *</label>
                <select className={inp} value={form.reason} onChange={(e) => set("reason", e.target.value)} data-testid="action-reason">
                  <option value="">— Select reason —</option>{standReasons.map((r) => <option key={r}>{r}</option>)}
                </select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Stand Down From</label>
                  <input type="date" className={inp} value={form.stand_down_from} onChange={(e) => set("stand_down_from", e.target.value)} data-testid="action-sd-from" /></div>
                <div><label className={lbl}>Stand Down To</label>
                  <input type="date" className={inp} value={form.stand_down_to} onChange={(e) => set("stand_down_to", e.target.value)} data-testid="action-sd-to" /></div>
              </div>
            </>
          )}

          {action === "edit_details" && (
            <>
              {assignment.kind === "vehicle" && (
                <div><label className={lbl}>Driver / Worker</label>
                  <select className={inp} value={form.driver_id} onChange={(e) => set("driver_id", e.target.value)} data-testid="action-driver">
                    <option value="">— No driver —</option>
                    {workers.map((w) => <option key={w.id} value={w.id}>{w.name}{w.onProject ? " (on this project)" : ""}</option>)}
                  </select></div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Start Date</label>
                  <input type="date" className={inp} value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="action-start-date" /></div>
                <div><label className={lbl}>End Date</label>
                  <input type="date" className={inp} value={form.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="action-end-date" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Supplier Rate Type</label>
                  <select className={inp} value={form.sup_rate_type} onChange={(e) => set("sup_rate_type", e.target.value)} data-testid="action-sup-rate-type">
                    <option value="">—</option>{rateTypes.map((r) => <option key={r}>{r}</option>)}
                  </select></div>
                <div><label className={lbl}>Supplier Rate ($)</label>
                  <input type="number" className={inp} value={form.sup_rate} onChange={(e) => set("sup_rate", e.target.value)} data-testid="action-sup-rate" /></div>
                <div><label className={lbl}>Customer Rate Type</label>
                  <select className={inp} value={form.cust_rate_type} onChange={(e) => set("cust_rate_type", e.target.value)} data-testid="action-cust-rate-type">
                    <option value="">—</option>{rateTypes.map((r) => <option key={r}>{r}</option>)}
                  </select></div>
                <div><label className={lbl}>Customer Rate ($)</label>
                  <input type="number" className={inp} value={form.cust_rate} onChange={(e) => set("cust_rate", e.target.value)} data-testid="action-cust-rate" /></div>
              </div>
              <div><label className={lbl}>Notes</label>
                <textarea className={inp + " h-20 py-1"} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="action-notes" /></div>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
          <button onClick={onClose} className="h-9 px-4 border border-border-control bg-white text-equipr-black text-[12px] uppercase tracking-[0.04em] font-semibold rounded-[7px] hover:bg-surface-panel active:bg-surface-pressed">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="action-modal-confirm"
            className={`h-9 px-5 text-sm font-bold rounded-none disabled:opacity-60 ${meta.color === "danger" ? "bg-red-600 text-white hover:bg-red-700" : "bg-equipr-yellow text-equipr-black hover:bg-equipr-yellow-dark"}`}>
            {saving ? "Saving…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ----------------------------- Editable cell ------------------------------ */
function EditableCell({ a, projectId, colKey, lookups, onChange }) {
  const col = COLUMN_CATALOG[colKey];
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");

  const start = (e) => {
    e.stopPropagation();
    const cur = a[colKey];
    setVal(cur == null ? "" : cur);
    setEditing(true);
  };

  const commit = async () => {
    setEditing(false);
    let payloadVal = val;
    if (col.edit.type === "number") payloadVal = val === "" ? null : Number(val);
    const prev = a[colKey] ?? (col.edit.type === "number" ? null : "");
    if (payloadVal === prev) return;
    try { await api.put(`/projects/${projectId}/assign/${a.id}`, { [colKey]: payloadVal }); onChange(); } catch { /* ignore */ }
  };

  if (!editing) {
    return (
      <button onClick={start} data-testid={`edit-cell-${colKey}-${a.id}`}
        className="w-full text-left truncate -mx-1 px-1 rounded-sm border-b border-dashed border-transparent hover:border-gray-400 hover:bg-equipr-yellow/10">
        {col.render(a)}
      </button>
    );
  }

  const cls = "w-full h-7 px-1 border border-equipr-black text-[12px] rounded-none focus:outline-none focus:ring-1 focus:ring-equipr-yellow";
  const onKey = (e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); };

  if (col.edit.type === "lookup") {
    const opts = (lookups && lookups[col.edit.lookup]) || [];
    return (
      <select autoFocus className={cls} value={val} onClick={(e) => e.stopPropagation()}
        onChange={(e) => setVal(e.target.value)} onBlur={commit} onKeyDown={onKey} data-testid={`edit-input-${colKey}-${a.id}`}>
        <option value="">—</option>{opts.map((r) => <option key={r}>{r}</option>)}
      </select>
    );
  }
  return (
    <input autoFocus type={col.edit.type === "number" ? "number" : col.edit.type === "date" ? "date" : "text"}
      className={cls} value={val} onClick={(e) => e.stopPropagation()}
      onChange={(e) => setVal(e.target.value)} onBlur={commit} onKeyDown={onKey}
      data-testid={`edit-input-${colKey}-${a.id}`} />
  );
}

/* -------------------------- Allocation row drawer ------------------------- */
function AllocationDrawer({ alloc: a, project, onClose, onChanged }) {
  const { isWriter } = useAuth();
  const [dockets, setDockets] = useState(null);
  const [action, setAction] = useState(null);
  const kindLabel = a.kind === "operator" ? "Worker" : a.kind === "vehicle" ? "Vehicle" : "Asset";
  const typeLabel = a.kind === "operator" ? "Worker" : (a.asset_type || "—");

  useEffect(() => {
    api.get("/dockets", { params: { project_id: a.projectId || project.id } })
      .then(({ data }) => {
        const mine = (data || []).filter((d) => a.kind === "operator" ? (d.worker === a.label) : (d.asset_no === a.label));
        mine.sort((x, y) => (y.date || "").localeCompare(x.date || ""));
        setDockets(mine.slice(0, 5));
      })
      .catch(() => setDockets([]));
  }, [a, project]);

  const Field = ({ k, v, wide }) => (
    <div className={wide ? "col-span-2" : ""}>
      <div className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-ink-muted mb-1">{k}</div>
      <div className="text-[15px] font-medium text-ink-secondary leading-snug">{v || "—"}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="allocation-drawer">
      <div className="flex-1 bg-[rgba(26,26,26,0.34)]" onClick={onClose} />
      <div className="w-full max-w-[460px] h-full bg-surface-card border-l border-border-warm shadow-modal flex flex-col animate-[slideIn_.18s_ease]" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-5 pb-4 border-b border-divider-inner shrink-0 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">{kindLabel} allocation</div>
            <h3 className="text-[24px] font-bold text-ink-primary leading-tight mt-0.5 truncate">{a.label}</h3>
            <p className="text-[13px] text-ink-tertiary mt-1 font-mono">{typeLabel} · #{project.project_no}</p>
          </div>
          <button onClick={onClose} data-testid="allocation-drawer-close" className="w-9 h-9 flex items-center justify-center rounded-[9px] border border-border-control text-ink-secondary hover:bg-surface-panel shrink-0"><X size={16} weight="bold" /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto bg-surface-inset flex-1">
          <section className="bg-surface-card border border-border rounded-[12px] overflow-hidden">
            <div className="px-4 py-3 border-b border-divider-inner"><h4 className="font-bold text-[15px] text-ink-primary">Allocation</h4></div>
            <div className="p-4 grid grid-cols-2 gap-x-6 gap-y-4">
              <Field k="Project" v={project.project_name || project.site_address} />
              <Field k="Supplier" v={a.supplier} />
              <Field k="Hire type" v={a.hire_type} />
              <Field k="Type" v={typeLabel} />
              <Field k="Start" v={a.start_date ? fmtDate(a.start_date) : "—"} />
              <Field k="End" v={a.end_date ? fmtDate(a.end_date) : "—"} />
            </div>
          </section>

          <section className="bg-surface-card border border-border rounded-[12px] overflow-hidden" data-testid="allocation-drawer-dockets">
            <div className="px-4 py-3 border-b border-divider-inner"><h4 className="font-bold text-[15px] text-ink-primary">Recent dockets</h4></div>
            {dockets === null ? (
              <div className="px-4 py-6 text-center text-[13px] text-ink-tertiary">Loading…</div>
            ) : dockets.length === 0 ? (
              <div className="px-4 py-6 text-center text-[13px] text-ink-tertiary">No dockets recorded yet.</div>
            ) : dockets.map((d) => (
              <div key={d.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-divider-row last:border-0">
                <span className="font-mono text-[13px] font-semibold text-ink-primary shrink-0">{d.docket_no}</span>
                <span className="font-mono text-[12px] text-ink-tertiary shrink-0">{fmtDate(d.date)}</span>
                <span className="text-[13px] text-ink-secondary flex-1 truncate">{d.notes || (d.task_entries && d.task_entries[0]?.name) || "—"}</span>
                <span className="font-mono text-[13px] font-semibold text-ink-primary shrink-0">{d.worked_hours ?? "—"}</span>
              </div>
            ))}
          </section>
        </div>

        {isWriter && (
          <div className="flex items-center gap-3 px-5 py-4 border-t border-border bg-surface-panel shrink-0">
            <button onClick={() => setAction("edit_details")} data-testid="allocation-reassign"
              className="h-11 px-5 bg-equipr-yellow text-black text-[12px] uppercase tracking-[0.05em] font-bold rounded-[10px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px">Reassign</button>
            <button onClick={() => { window.location.href = a.kind === "operator" ? "/operators" : "/assets"; }} data-testid="allocation-open-record"
              className="h-11 px-5 border border-border-control bg-white text-ink-primary text-[12px] uppercase tracking-[0.05em] font-bold rounded-[10px] hover:bg-surface-panel">Open Record</button>
            <div className="flex-1 text-right">
              <span className="block text-[11px] text-ink-tertiary leading-tight">{a.kind === "operator" ? "Releases them from the schedule; they stay on the register" : "Removes it from the schedule and notifies the supplier"}</span>
              <button onClick={() => setAction(a.kind === "operator" ? "demobilise" : "off_hire")} data-testid="allocation-off-hire" className="text-[13px] font-bold text-red-600 hover:underline mt-0.5">{a.kind === "operator" ? "Demobilise this worker" : "Off hire this allocation"}</button>
            </div>
          </div>
        )}
      </div>
      {action && <ActionModal action={action} assignment={a} projectId={a.projectId || project.id} onClose={() => setAction(null)} onDone={() => { setAction(null); onChanged?.(); onClose(); }} />}
    </div>
  );
}


/* ------------------------- Assignment row + actions ----------------------- */
function BulkActionModal({ action, items, onClose, onDone }) {
  const offReasons = useLookup("offhire_reasons");
  const standReasons = useLookup("standdown_reasons");
  const today = new Date().toISOString().slice(0, 10);
  const isOff = action === "off_hire";
  const isDemob = action === "demobilise";
  const needsReason = !isDemob;
  const reasons = isOff ? offReasons : standReasons;
  const [reason, setReason] = useState("");
  const [offDate, setOffDate] = useState(today);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(0);
  const inp = "w-full h-9 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none";
  const lbl = "text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted mb-1.5 block";

  const run = async () => {
    if (needsReason && !reason) { setError("Choose a reason first."); return; }
    setBusy(true); setError(""); setDone(0);
    let failed = 0;
    for (const it of items) {
      try {
        if (isOff) {
          await api.post("/offhires", { asset_no: it.label, supplier: it.supplier, project_id: it.projectId, reason, off_hire_date: offDate });
          await api.put(`/projects/${it.projectId}/assign/${it.id}`, { status: "off_hire", end_date: offDate });
        } else if (isDemob) {
          await api.put(`/projects/${it.projectId}/assign/${it.id}`, { status: "demobilised", end_date: offDate });
        } else {
          await api.post("/standdowns", { asset_no: it.label, supplier: it.supplier, project_id: it.projectId, reason, stand_down_from: from, stand_down_to: to });
          await api.put(`/projects/${it.projectId}/assign/${it.id}`, { status: "stand_down", stand_down_from: from, stand_down_to: to });
        }
      } catch { failed += 1; }
      setDone((d) => d + 1);
    }
    setBusy(false);
    if (failed) setError(`${failed} of ${items.length} could not be updated. The rest were applied — close and retry the remainder.`);
    else onDone();
  };
  const title = isOff ? "Off Hire" : isDemob ? "Demobilise" : "Stand Down";

  return (
    <div className="fixed inset-0 z-[60] bg-[rgba(26,26,26,0.42)] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface-panel border border-border-warm rounded-[14px] shadow-modal w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="bulk-action-modal">
        <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-divider-inner">
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">Bulk action</div>
            <h3 className="font-bold text-[19px] text-ink-primary leading-tight mt-0.5">{title} · {items.length} {isDemob ? "worker" : "asset"}{items.length === 1 ? "" : "s"}</h3>
          </div>
          <button onClick={onClose} data-testid="bulk-modal-close" className="w-9 h-9 flex items-center justify-center rounded-[9px] border border-border-control text-ink-secondary hover:bg-surface-panel"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-5 space-y-4">
          {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-[8px]" data-testid="bulk-error">{error}</div>}
          <div className="max-h-28 overflow-auto bg-surface-inset border border-border rounded-[8px] p-2 flex flex-wrap gap-1.5">
            {items.map((it) => <span key={it.id} className="text-[11px] font-mono bg-surface-card border border-border rounded-[4px] px-1.5 py-0.5 text-ink-secondary">{it.label}</span>)}
          </div>
          {needsReason && (
            <div><label className={lbl}>Reason *</label>
              <select className={inp} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="bulk-reason-select">
                <option value="">— Select a reason —</option>
                {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
              </select></div>
          )}
          {(isOff || isDemob) ? (
            <div><label className={lbl}>{isDemob ? "Demobilise date" : "Off hire date"}</label>
              <input type="date" className={inp} value={offDate} onChange={(e) => setOffDate(e.target.value)} data-testid="bulk-offhire-date" /></div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>From</label><input type="date" className={inp} value={from} onChange={(e) => setFrom(e.target.value)} data-testid="bulk-sd-from" /></div>
              <div><label className={lbl}>To</label><input type="date" className={inp} value={to} onChange={(e) => setTo(e.target.value)} data-testid="bulk-sd-to" /></div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-divider-inner bg-surface-panel">
          <button onClick={onClose} className="h-9 px-4 border border-border-control bg-white text-equipr-black text-[12px] uppercase tracking-[0.04em] font-semibold rounded-[8px] hover:bg-surface-panel">Cancel</button>
          <button onClick={run} disabled={busy} data-testid="bulk-confirm-btn"
            className="h-9 px-5 bg-equipr-yellow text-black text-[12px] uppercase tracking-[0.04em] font-bold rounded-[8px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px disabled:opacity-60">
            {busy ? `Processing ${done}/${items.length}…` : `${title} ${items.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkReallocateModal({ items, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [projects, setProjects] = useState([]);
  const [sites, setSites] = useState([]);
  const [destType, setDestType] = useState("project");   // project | site
  const [toId, setToId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(0);
  const inp = "w-full h-9 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none";
  const lbl = "text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted mb-1.5 block";
  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(Array.isArray(data) ? data : (data.items || []))).catch(() => {});
    api.get("/sites").then(({ data }) => setSites(Array.isArray(data) ? data : (data.items || []))).catch(() => {});
  }, []);

  const pickType = (t) => { setDestType(t); setToId(""); };

  const run = async () => {
    if (!toId) { setError(destType === "project" ? "Choose a destination project." : "Choose a destination depot / site."); return; }
    setBusy(true); setError(""); setDone(0);
    let failed = 0;
    if (destType === "project") {
      for (const it of items) {
        try {
          if (it.projectId && it.projectId !== toId) {
            await api.put(`/projects/${it.projectId}/assign/${it.id}`, { status: "off_hire", end_date: today });
          }
          await api.post(`/projects/${toId}/assign`, { kind: it.kind, ref_id: it.ref_id });
        } catch { failed += 1; }
        setDone((d) => d + 1);
      }
    } else {
      // Reallocate to a depot/site: off-hire from the current project + arrange transport back to the site.
      const dest = sites.find((s) => s.id === toId);
      const groups = {};
      for (const it of items) {
        try {
          if (it.projectId && it.status !== "off_hire" && it.status !== "demobilised") {
            await api.put(`/projects/${it.projectId}/assign/${it.id}`, { status: "off_hire", end_date: today });
          }
          (groups[it.projectId] = groups[it.projectId] || []).push(it.label);
        } catch { failed += 1; }
        setDone((d) => d + 1);
      }
      for (const [pid, labels] of Object.entries(groups)) {
        try {
          await api.post("/transportation", {
            asset_nos: labels, project_id: pid,
            from_type: "project", from_id: pid, from_location: "Project site",
            to_type: "site", to_id: toId, to_location: dest?.name || "Depot",
            scheduled_at: today, status: "scheduled", notes: "Reallocated to depot",
          });
        } catch { failed += 1; }
      }
    }
    setBusy(false);
    if (failed) setError(`${failed} action(s) could not be completed.`);
    else onDone();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[rgba(26,26,26,0.42)] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface-panel border border-border-warm rounded-[14px] shadow-modal w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="bulk-reallocate-modal">
        <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-divider-inner">
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">Bulk action</div>
            <h3 className="font-bold text-[19px] text-ink-primary leading-tight mt-0.5">Reallocate · {items.length} asset{items.length === 1 ? "" : "s"}</h3>
          </div>
          <button onClick={onClose} data-testid="bulk-reallocate-close" className="w-9 h-9 flex items-center justify-center rounded-[9px] border border-border-control text-ink-secondary hover:bg-surface-panel"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-5 space-y-4">
          {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-[8px]" data-testid="bulk-reallocate-error">{error}</div>}
          <div className="max-h-24 overflow-auto bg-surface-inset border border-border rounded-[8px] p-2 flex flex-wrap gap-1.5">
            {items.map((it) => <span key={it.id} className="text-[11px] font-mono bg-surface-card border border-border rounded-[4px] px-1.5 py-0.5 text-ink-secondary">{it.label}</span>)}
          </div>
          <div>
            <label className={lbl}>Destination type</label>
            <div className="inline-flex rounded-[8px] border border-border-control overflow-hidden" data-testid="bulk-reallocate-desttype">
              <button type="button" onClick={() => pickType("project")} data-testid="bulk-reallocate-type-project"
                className={`px-4 h-9 text-[12px] font-bold uppercase tracking-[0.04em] ${destType === "project" ? "bg-equipr-yellow text-black" : "bg-white text-ink-secondary hover:bg-surface-panel"}`}>Project</button>
              <button type="button" onClick={() => pickType("site")} data-testid="bulk-reallocate-type-site"
                className={`px-4 h-9 text-[12px] font-bold uppercase tracking-[0.04em] border-l border-border-control ${destType === "site" ? "bg-equipr-yellow text-black" : "bg-white text-ink-secondary hover:bg-surface-panel"}`}>Depot / Site</button>
            </div>
          </div>
          {destType === "project" ? (
            <>
              <p className="text-[12px] text-ink-tertiary -mt-1">Assets on another project are off-hired there and re-hired onto the destination.</p>
              <div><label className={lbl}>Destination project *</label>
                <select className={inp} value={toId} onChange={(e) => setToId(e.target.value)} data-testid="bulk-reallocate-dest">
                  <option value="">— Select a project —</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.project_name || p.site_address || p.project_no || p.id}</option>)}
                </select></div>
            </>
          ) : (
            <>
              <p className="text-[12px] text-ink-tertiary -mt-1">Assets are off-hired from their project and a transport back to the depot / site is scheduled.</p>
              <div><label className={lbl}>Destination depot / site *</label>
                <select className={inp} value={toId} onChange={(e) => setToId(e.target.value)} data-testid="bulk-reallocate-dest-site">
                  <option value="">— Select a site —</option>
                  {sites.map((s) => <option key={s.id} value={s.id}>{s.name}{s.site_type ? ` · ${s.site_type}` : ""}</option>)}
                </select></div>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-divider-inner bg-surface-panel">
          <button onClick={onClose} className="h-9 px-4 border border-border-control bg-white text-equipr-black text-[12px] uppercase tracking-[0.04em] font-semibold rounded-[8px] hover:bg-surface-panel">Cancel</button>
          <button onClick={run} disabled={busy} data-testid="bulk-reallocate-confirm"
            className="h-9 px-5 bg-equipr-yellow text-black text-[12px] uppercase tracking-[0.04em] font-bold rounded-[8px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px disabled:opacity-60">
            {busy ? `Working ${done}/${items.length}…` : `Reallocate ${items.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkTransportModal({ items, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [sites, setSites] = useState([]);
  const [projects, setProjects] = useState([]);
  const [destType, setDestType] = useState("site");   // site | project
  const [toId, setToId] = useState("");
  const [date, setDate] = useState(today);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(0);
  const inp = "w-full h-9 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none";
  const lbl = "text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted mb-1.5 block";

  useEffect(() => {
    api.get("/sites").then(({ data }) => {
      const list = Array.isArray(data) ? data : (data.items || []);
      setSites(list);
      const depot = list.find((s) => /depot/i.test(s.name || "") || /depot|yard/i.test(s.site_type || "")) || list[0];
      if (depot) setToId(depot.id);
    }).catch(() => {});
    api.get("/projects").then(({ data }) => setProjects(Array.isArray(data) ? data : (data.items || []))).catch(() => {});
  }, []);

  const pickType = (t) => { setDestType(t); if (t === "site") { const d = sites.find((s) => /depot/i.test(s.name || "") || /depot|yard/i.test(s.site_type || "")) || sites[0]; setToId(d ? d.id : ""); } else setToId(""); };

  const groups = {};
  items.forEach((it) => { (groups[it.projectId] = groups[it.projectId] || []).push(it.label); });
  const groupCount = Object.keys(groups).length;
  const destName = destType === "site"
    ? (sites.find((s) => s.id === toId)?.name || "Depot")
    : (() => { const p = projects.find((x) => x.id === toId); return p ? (p.project_name || p.site_address || p.project_no) : "Project"; })();

  const run = async () => {
    if (!toId) { setError(destType === "site" ? "Choose a destination depot / yard." : "Choose a destination project."); return; }
    setBusy(true); setError(""); setDone(0);
    let failed = 0;
    const entries = Object.entries(groups);
    for (const [pid, labels] of entries) {
      try {
        await api.post("/transportation", {
          asset_nos: labels, project_id: pid,
          from_type: "project", from_id: pid, from_location: "Project site",
          to_type: destType, to_id: toId, to_location: destName,
          scheduled_at: date, status: "scheduled", notes: notes || (destType === "site" ? "Bulk transport to depot" : "Bulk transport to project"),
        });
      } catch { failed += 1; }
      setDone((d) => d + 1);
    }
    setBusy(false);
    if (failed) setError(`${failed} of ${entries.length} movement(s) could not be scheduled.`);
    else onDone();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[rgba(26,26,26,0.42)] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface-panel border border-border-warm rounded-[14px] shadow-modal w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="bulk-transport-modal">
        <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-divider-inner">
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">Bulk action</div>
            <h3 className="font-bold text-[19px] text-ink-primary leading-tight mt-0.5">Transport · {items.length} asset{items.length === 1 ? "" : "s"}</h3>
          </div>
          <button onClick={onClose} data-testid="bulk-transport-close" className="w-9 h-9 flex items-center justify-center rounded-[9px] border border-border-control text-ink-secondary hover:bg-surface-panel"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-5 space-y-4">
          {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-[8px]" data-testid="bulk-transport-error">{error}</div>}
          <div className="max-h-24 overflow-auto bg-surface-inset border border-border rounded-[8px] p-2 flex flex-wrap gap-1.5">
            {items.map((it) => <span key={it.id} className="text-[11px] font-mono bg-surface-card border border-border rounded-[4px] px-1.5 py-0.5 text-ink-secondary">{it.label}</span>)}
          </div>
          <p className="text-[12px] text-ink-tertiary -mt-1">{groupCount} movement{groupCount === 1 ? "" : "s"} will be scheduled (one per source project).</p>
          <div>
            <label className={lbl}>Destination type</label>
            <div className="inline-flex rounded-[8px] border border-border-control overflow-hidden" data-testid="bulk-transport-desttype">
              <button type="button" onClick={() => pickType("site")} data-testid="bulk-transport-type-site"
                className={`px-4 h-9 text-[12px] font-bold uppercase tracking-[0.04em] ${destType === "site" ? "bg-equipr-yellow text-black" : "bg-white text-ink-secondary hover:bg-surface-panel"}`}>Depot / Yard</button>
              <button type="button" onClick={() => pickType("project")} data-testid="bulk-transport-type-project"
                className={`px-4 h-9 text-[12px] font-bold uppercase tracking-[0.04em] border-l border-border-control ${destType === "project" ? "bg-equipr-yellow text-black" : "bg-white text-ink-secondary hover:bg-surface-panel"}`}>Project</button>
            </div>
          </div>
          {destType === "site" ? (
            <div><label className={lbl}>Destination depot / yard *</label>
              <select className={inp} value={toId} onChange={(e) => setToId(e.target.value)} data-testid="bulk-transport-dest">
                <option value="">— Select a site —</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}{s.site_type ? ` · ${s.site_type}` : ""}</option>)}
              </select></div>
          ) : (
            <div><label className={lbl}>Destination project *</label>
              <select className={inp} value={toId} onChange={(e) => setToId(e.target.value)} data-testid="bulk-transport-dest-project">
                <option value="">— Select a project —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.project_name || p.site_address || p.project_no || p.id}</option>)}
              </select></div>
          )}
          <div><label className={lbl}>Scheduled date</label>
            <input type="date" className={inp} value={date} onChange={(e) => setDate(e.target.value)} data-testid="bulk-transport-date" /></div>
          <div><label className={lbl}>Notes</label>
            <input className={inp} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional — carrier, instructions…" data-testid="bulk-transport-notes" /></div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-divider-inner bg-surface-panel">
          <button onClick={onClose} className="h-9 px-4 border border-border-control bg-white text-equipr-black text-[12px] uppercase tracking-[0.04em] font-semibold rounded-[8px] hover:bg-surface-panel">Cancel</button>
          <button onClick={run} disabled={busy} data-testid="bulk-transport-confirm"
            className="h-9 px-5 bg-equipr-yellow text-black text-[12px] uppercase tracking-[0.04em] font-bold rounded-[8px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px disabled:opacity-60">
            {busy ? `Scheduling ${done}/${groupCount}…` : `Schedule transport`}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignmentRow({ a, projectId, project, onChange, columns, lookups, rowPad = "py-2", sel, onToggleSelect }) {
  const { isWriter } = useAuth();
  const drawer = React.useContext(RowDrawerCtx);
  const [menu, setMenu] = useState(false);
  const [action, setAction] = useState(null);

  const open = (type) => { setMenu(false); setAction(type); };
  const remove = async () => { setMenu(false); await api.delete(`/projects/${projectId}/assign/${a.id}`); onChange(); };
  const rehire = async () => { setMenu(false); await api.put(`/projects/${projectId}/assign/${a.id}`, { status: "active" }); onChange(); };
  const rowClick = (e) => {
    if (e.target.closest("button, input, select, textarea, a")) return;
    drawer.open({ ...a, projectId }, project);
  };

  const eff = effStatus(a);
  const isException = ["stand_down", "off_hire", "maintenance", "demobilised"].includes(eff);
  const ended = a.status === "off_hire" || a.status === "demobilised";
  return (
    <>
      <tr onClick={rowClick} className={`border-b border-divider-row hover:bg-accent-wash group cursor-pointer ${isException ? "shadow-[inset_3px_0_0_#FF4F00]" : ""}`} data-testid={`assignment-${a.id}`}>
        {columns.map((k, i) => {
          const body = (isWriter && COLUMN_CATALOG[k].edit && !ended && !(k === "hire_type" && a.kind === "operator"))
            ? <EditableCell a={a} projectId={projectId} colKey={k} lookups={lookups} onChange={onChange} />
            : COLUMN_CATALOG[k].render(a);
          return (
            <td key={k} className={`px-3 ${rowPad} text-cell text-ink-secondary truncate ${i === 0 ? "text-ink-primary" : ""}`}>
              {i === 0 && (a.kind === "asset" || a.kind === "operator") && onToggleSelect ? (
                <span className="flex items-center gap-2 min-w-0">
                  <input type="checkbox" checked={!!sel?.has(a.id)} onClick={(e) => e.stopPropagation()}
                    onChange={() => onToggleSelect(a, projectId)} data-testid={`bulk-select-${a.id}`}
                    className="w-3.5 h-3.5 accent-equipr-yellow shrink-0" />
                  <span className="truncate min-w-0">{body}</span>
                </span>
              ) : body}
            </td>
          );
        })}
        <td className={`px-3 ${rowPad} text-right relative`}>
          {isWriter && (
            <button onClick={() => setMenu((m) => !m)} data-testid={`assignment-menu-${a.id}`} className="text-ink-tertiary hover:text-ink-primary opacity-0 group-hover:opacity-100">
              <DotsThreeVertical size={18} weight="bold" />
            </button>
          )}
          {menu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
              <div className="absolute right-2 top-8 z-20 bg-surface-card border border-border-warm rounded-[12px] shadow-modal w-56 text-left overflow-hidden">
                <div className="px-4 pt-3 pb-2.5 border-b border-divider-inner">
                  <div className="font-mono text-[14px] font-bold text-ink-primary truncate">{a.label}</div>
                  <div className="text-[12px] text-ink-tertiary">{a.kind === "operator" ? "Worker" : a.kind === "vehicle" ? "Vehicle" : "Plant"}</div>
                </div>
                <div className="py-1">
                  {ended && (
                    <button onClick={rehire} data-testid={`action-rehire-${a.id}`} className="w-full text-left px-4 py-2 text-[14px] font-semibold text-emerald-700 hover:bg-emerald-50">{a.kind === "operator" ? "Re-mobilise worker" : "Re-hire asset"}</button>
                  )}
                  {(a.kind === "operator"
                    ? [["Edit line details", "edit_details"], ["Demobilise", "demobilise"], ["Stand down", "stand_down"]]
                    : [["Edit line details", "edit_details"], ["Off hire", "off_hire"], ["Stand down", "stand_down"]]
                  ).filter(([, t]) => !(ended && (t === "off_hire" || t === "demobilise"))).map(([l, t]) => (
                    <button key={t} onClick={() => open(t)} data-testid={`action-${t}-${a.id}`} className="w-full text-left px-4 py-2 text-[14px] text-ink-primary hover:bg-accent-wash">{l}</button>
                  ))}
                </div>
                <button onClick={remove} className="w-full text-left px-4 py-2.5 text-[14px] font-semibold text-red-600 hover:bg-red-50 border-t border-divider-inner">Remove from project</button>
              </div>
            </>
          )}
        </td>
      </tr>
      {action && (
        <ActionModal action={action} assignment={a} projectId={projectId}
          onClose={() => setAction(null)} onDone={onChange} />
      )}
    </>
  );
}

/* ---------------------------- Droppable project --------------------------- */
function ProjectGroup({ project, onChange, onEdit, selected, onSelect, columns, lookups, listQ, sort, filterActive, collapsed, onToggleCollapse, rowPad, sel, onToggleSelect, onToggleMany }) {
  const { isWriter, user } = useAuth();
  const { setNodeRef, isOver } = useDroppable({ id: `project:${project.id}`, data: { projectId: project.id } });
  const scope = user?.role === "admin" ? "both" : (user?.resource_scope || "both");
  let all = project.assignments || [];
  if (scope === "assets") all = all.filter((a) => a.kind === "asset" || a.kind === "vehicle");
  else if (scope === "workers") all = all.filter((a) => a.kind === "operator");
  const rows = applyFilterSort(all, listQ, sort);
  const assetRows = rows.filter((a) => a.kind === "asset");
  const allAssetsSelected = assetRows.length > 0 && assetRows.every((a) => sel?.has(a.id));
  const toggleCollapse = (e) => { e.stopPropagation(); onToggleCollapse(project.id); };
  if (filterActive && rows.length === 0) return null;
  const title = project.project_name || project.site_address;
  return (
    <div ref={setNodeRef} onClick={() => onSelect(project.id)}
      data-testid={`project-group-${project.id}`}
      className={`border-b border-divider-inner ${isOver ? "equipr-drag-over" : ""}`}>
      <div className={`flex items-center justify-between px-3 py-2.5 border-b border-divider-row bg-surface-sunken ${selected ? "shadow-[inset_3px_0_0_#FFD108]" : ""}`}>
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={toggleCollapse} data-testid={`collapse-project-${project.id}`}
            title={collapsed ? "Expand" : "Collapse"}
            className="text-ink-tertiary hover:text-ink-primary shrink-0 -ml-1 p-0.5">
            {collapsed ? <CaretRight size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
          </button>
          <span className="font-bold text-subhead text-ink-primary truncate">{title}</span>
          {project.project_name && <span className="text-meta text-ink-tertiary truncate">· {project.site_address}</span>}
          <span className="text-meta text-ink-tertiary truncate">· {project.client}</span>
          <span className="font-mono text-mono-id text-ink-tertiary">#{project.project_no}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isWriter && assetRows.length > 0 && onToggleMany && (
            <label className="flex items-center gap-1.5 text-meta text-ink-tertiary cursor-pointer" title="Select all assets in this project" onClick={(e) => e.stopPropagation()}>
              <input type="checkbox" checked={allAssetsSelected} data-testid={`bulk-select-all-${project.id}`}
                onChange={(e) => onToggleMany(assetRows, project.id, e.target.checked)}
                className="w-3.5 h-3.5 accent-equipr-yellow" />
              all assets
            </label>
          )}
          <span className="text-meta text-ink-tertiary">{filterActive ? `${rows.length}/${all.length}` : all.length} assigned</span>
          {isWriter && <button onClick={(e) => { e.stopPropagation(); onEdit(project); }} data-testid={`edit-project-${project.id}`} className="text-ink-tertiary hover:text-ink-primary"><PencilSimple size={15} /></button>}
        </div>
      </div>
      {!collapsed && (
      <table className="w-full text-[13px] table-fixed">
        <ColGroup columns={columns} />
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length + 1} className="px-3 py-4 text-center text-ink-tertiary text-cell">Drag assets or workers here to allocate.</td></tr>
          ) : rows.map((a) => (
            <AssignmentRow key={a.id} a={a} projectId={project.id} project={project} onChange={onChange} columns={columns} lookups={lookups} rowPad={rowPad} sel={sel} onToggleSelect={onToggleSelect} />
          ))}
        </tbody>
      </table>
      )}
    </div>
  );
}

/* ------------------------------ Timeline view ----------------------------- */
const DAY_MS = 86400000;
const toISO = (d) => d.toISOString().slice(0, 10);
const parseDay = (s) => { const d = new Date(s); d.setHours(0, 0, 0, 0); return d; };
const addDays = (d, n) => new Date(d.getTime() + n * DAY_MS);
const GRAN = { day: { days: 28, step: 14 }, week: { days: 84, step: 28 }, month: { days: 183, step: 30 } };

function TimelineView({ projects, onChange }) {
  const { user } = useAuth();
  const scope = user?.role === "admin" ? "both" : (user?.resource_scope || "both");
  const [gran, setGran] = useState("day");
  const [anchor, setAnchor] = useState(() => { const b = new Date(); b.setHours(0, 0, 0, 0); return b; });
  const [standdowns, setStanddowns] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const windowDays = GRAN[gran].days;
  const start = anchor;
  const windowEnd = useMemo(() => addDays(start, windowDays), [start, windowDays]);

  const trackRef = React.useRef(null);
  const [trackW, setTrackW] = useState(1);
  const [drag, setDrag] = useState(null);
  const [preview, setPreview] = useState({});

  React.useEffect(() => {
    api.get("/standdowns").then(({ data }) => setStanddowns(data)).catch(() => {});
    api.get("/vehicles").then(({ data }) => setVehicles(data)).catch(() => {});
  }, [projects]);

  React.useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setTrackW(el.clientWidth));
    ro.observe(el);
    setTrackW(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const pxPerDay = trackW / windowDays;

  // header columns depending on granularity
  const cols = useMemo(() => {
    const out = [];
    if (gran === "day") {
      for (let i = 0; i < windowDays; i++) {
        const d = addDays(start, i);
        out.push({ widthPct: 100 / windowDays, top: d.toLocaleDateString("en-AU", { weekday: "narrow" }),
          sub: `${d.getDate()}/${d.getMonth() + 1}`, weekend: [0, 6].includes(d.getDay()) });
      }
    } else if (gran === "week") {
      const n = Math.round(windowDays / 7);
      for (let w = 0; w < n; w++) {
        const d = addDays(start, w * 7);
        out.push({ widthPct: 100 / n, top: "W/C", sub: `${d.getDate()}/${d.getMonth() + 1}`, weekend: false });
      }
    } else {
      let cursor = new Date(start);
      while (cursor < windowEnd) {
        const nextMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
        const segEnd = nextMonth < windowEnd ? nextMonth : windowEnd;
        const dcount = Math.max(1, Math.round((segEnd - cursor) / DAY_MS));
        out.push({ widthPct: (dcount / windowDays) * 100,
          top: cursor.toLocaleDateString("en-AU", { month: "short" }),
          sub: String(cursor.getFullYear()).slice(2), weekend: false });
        cursor = nextMonth;
      }
    }
    return out;
  }, [gran, start, windowDays, windowEnd]);

  const rows = useMemo(() => {
    const r = [];
    projects.forEach((p) => (p.assignments || []).forEach((a) => {
      if (scope === "assets" && !(a.kind === "asset" || a.kind === "vehicle")) return;
      if (scope === "workers" && a.kind !== "operator") return;
      r.push({ ...a, projectId: p.id, project: p });
    }));
    return r;
  }, [projects, scope]);

  const sdByKey = useMemo(() => {
    const m = {};
    standdowns.forEach((s) => { (m[`${s.project_id}|${s.asset_no}`] = m[`${s.project_id}|${s.asset_no}`] || []).push(s); });
    return m;
  }, [standdowns]);

  const conflicts = useMemo(() => {
    const set = new Set();
    const byRef = {};
    rows.forEach((a) => { (byRef[a.ref_id] = byRef[a.ref_id] || []).push(a); });
    Object.values(byRef).forEach((list) => {
      for (let i = 0; i < list.length; i++)
        for (let j = i + 1; j < list.length; j++) {
          const A = list[i], B = list[j];
          const as = parseDay(A.start_date || start), ae = parseDay(A.end_date || A.start_date || start);
          const bs = parseDay(B.start_date || start), be = parseDay(B.end_date || B.start_date || start);
          if (as <= be && bs <= ae) { set.add(A.id); set.add(B.id); }
        }
    });
    return set;
  }, [rows, start]);

  const driverConflicts = useMemo(() => {
    const set = new Set();
    const byDrv = {};
    rows.forEach((a) => {
      if (a.kind === "vehicle" && a.driver_id && a.status !== "off_hire")
        (byDrv[a.driver_id] = byDrv[a.driver_id] || []).push(a);
    });
    Object.values(byDrv).forEach((list) => {
      for (let i = 0; i < list.length; i++)
        for (let j = i + 1; j < list.length; j++) {
          const A = list[i], B = list[j];
          if (A.ref_id === B.ref_id) continue;
          const as = parseDay(A.start_date || start), ae = parseDay(A.end_date || A.start_date || start);
          const bs = parseDay(B.start_date || start), be = parseDay(B.end_date || B.start_date || start);
          if (as <= be && bs <= ae) { set.add(A.id); set.add(B.id); }
        }
    });
    return set;
  }, [rows, start]);

  const effDates = (a) => preview[a.id] || { start_date: a.start_date, end_date: a.end_date };

  // percentage position of a [from,to] date range within the window (clipped)
  const rangePct = (fromStr, toStr) => {
    const s = parseDay(fromStr), e = addDays(parseDay(toStr), 1); // exclusive end
    const lo = Math.max(s, start), hi = Math.min(e, windowEnd);
    if (hi <= lo) return null;
    const left = ((lo - start) / DAY_MS / windowDays) * 100;
    const width = ((hi - lo) / DAY_MS / windowDays) * 100;
    return { left: `${left}%`, width: `${width}%` };
  };

  const barStyle = (a) => {
    const { start_date, end_date } = effDates(a);
    const p = rangePct(start_date || toISO(start), end_date || start_date || toISO(start));
    return p || { left: "0%", width: "0%", display: "none" };
  };

  const beginDrag = (e, a, mode) => {
    e.stopPropagation();
    e.preventDefault();
    setDrag({ id: a.id, projectId: a.projectId, mode, startX: e.clientX,
      origStart: parseDay(a.start_date || start), origEnd: parseDay(a.end_date || a.start_date || start) });
  };

  React.useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const deltaDays = Math.round((e.clientX - drag.startX) / pxPerDay);
      let s = drag.origStart, en = drag.origEnd;
      if (drag.mode === "move") { s = addDays(drag.origStart, deltaDays); en = addDays(drag.origEnd, deltaDays); }
      else if (drag.mode === "resize-left") { s = addDays(drag.origStart, deltaDays); if (s > en) s = en; }
      else if (drag.mode === "resize-right") { en = addDays(drag.origEnd, deltaDays); if (en < s) en = s; }
      setPreview((p) => ({ ...p, [drag.id]: { start_date: toISO(s), end_date: toISO(en) } }));
    };
    const onUp = async () => {
      const pv = preview[drag.id];
      const d = drag;
      setDrag(null);
      if (pv) {
        try { await api.put(`/projects/${d.projectId}/assign/${d.id}`, { start_date: pv.start_date, end_date: pv.end_date }); onChange(); } catch { /* ignore */ }
        setPreview((p) => { const n = { ...p }; delete n[d.id]; return n; });
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
    return () => { window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
  }, [drag, pxPerDay, preview, onChange]);

  const shift = (dir) => setAnchor((a) => addDays(a, dir * GRAN[gran].step));
  const goToday = () => { const b = new Date(); b.setHours(0, 0, 0, 0); setAnchor(b); };
  const rangeLabel = `${start.toLocaleDateString("en-AU", { day: "2-digit", month: "short" })} – ${addDays(windowEnd, -1).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "2-digit" })}`;

  return (
    <div className="overflow-auto h-full">
      <div className="min-w-[1000px]">
        {/* Controls */}
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-divider-inner flex-wrap">
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-surface-inset rounded-[999px] p-0.5">
              {["day", "week", "month"].map((g) => (
                <button key={g} onClick={() => setGran(g)} data-testid={`timeline-gran-${g}`}
                  className={`px-3 h-8 rounded-[999px] text-[12px] font-bold uppercase tracking-[0.04em] transition-colors ${gran === g ? "bg-equipr-yellow text-black" : "text-ink-secondary hover:text-ink-primary"}`}>{g}</button>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => shift(-1)} data-testid="timeline-prev" className="h-8 w-8 border border-border-control text-ink-secondary hover:bg-surface-panel rounded-[7px] flex items-center justify-center"><CaretLeft size={14} weight="bold" /></button>
              <span className="text-[13px] font-mono font-semibold text-ink-primary min-w-[168px] text-center" data-testid="timeline-range">{rangeLabel}</span>
              <button onClick={() => shift(1)} data-testid="timeline-next" className="h-8 w-8 border border-border-control text-ink-secondary hover:bg-surface-panel rounded-[7px] flex items-center justify-center"><CaretRight size={14} weight="bold" /></button>
              <button onClick={goToday} data-testid="timeline-today" className="h-8 px-2.5 border border-border-control text-[11px] font-bold uppercase tracking-[0.04em] text-ink-secondary hover:bg-surface-panel rounded-[7px]">Today</button>
            </div>
          </div>
          <div className="flex items-center gap-4 text-[11.5px] text-ink-secondary" data-testid="timeline-legend">
            <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-[3px] bg-equipr-yellow" /> On hire</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-[3px] bg-[#FBE0E0] border border-[#E39B9B]" /> Stand down</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-[3px] bg-surface-inset border border-border" /> Off hire</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-[3px] bg-white border-2 border-[#E8622A]" /> Double-booked</span>
          </div>
        </div>
        <div className="flex sticky top-0 bg-surface-sunken z-10 border-b border-border">
          <div className="w-56 shrink-0 px-4 py-2.5 text-[11px] font-medium uppercase tracking-[0.1em] text-ink-muted border-r border-divider-inner">Resource</div>
          <div ref={trackRef} className="flex-1 flex">
            {cols.map((c, i) => (
              <div key={i} style={{ width: `${c.widthPct}%` }} className={`text-center py-2.5 text-[11px] uppercase tracking-[0.06em] border-r border-divider-row overflow-hidden ${c.weekend ? "bg-surface-inset text-ink-disabled" : "text-ink-muted"}`}>
                <div className="font-semibold truncate">{c.top}</div>
                {c.sub && <div className="truncate normal-case tracking-normal">{c.sub}</div>}
              </div>
            ))}
          </div>
        </div>
        {rows.length === 0 && <div className="p-8 text-center text-ink-tertiary text-sm">No allocations to show on the timeline.</div>}
        {rows.map((a) => {
          const conflict = conflicts.has(a.id);
          const drvConflict = driverConflicts.has(a.id);
          const { start_date, end_date } = effDates(a);
          const sds = sdByKey[`${a.projectId}|${a.label}`] || [];
          const barCls = conflict
            ? "bg-white border-2 border-[#E8622A] text-ink-primary"
            : (a.status === "off_hire" || a.status === "demobilised")
              ? "bg-surface-inset border border-border text-ink-secondary"
              : "bg-equipr-yellow text-black";
          return (
            <div key={a.id} className="flex border-b border-divider-row hover:bg-accent-wash" data-testid={`timeline-row-${a.id}`}>
              <div className="w-56 shrink-0 px-4 py-2 border-r border-divider-inner">
                <div className="text-[13px] font-semibold text-ink-primary truncate flex items-center gap-1.5">
                  {conflict && <span title="Double-booked" className="inline-block w-1.5 h-1.5 rounded-full bg-[#E8622A]" data-testid={`conflict-${a.id}`} />}
                  {drvConflict && <span title={`Driver ${a.driver_name} is on two vehicles for overlapping dates`} className="inline-block w-1.5 h-1.5 rounded-full bg-orange-500" data-testid={`driver-conflict-${a.id}`} />}
                  {a.label}
                </div>
                <div className="text-[11px] text-ink-tertiary truncate">{a.project.site_address}</div>
              </div>
              <div className="flex-1 relative min-h-[44px]">
                <div className="absolute inset-0 flex">
                  {cols.map((c, i) => <div key={i} style={{ width: `${c.widthPct}%` }} className={`border-r border-divider-row ${c.weekend ? "bg-surface-inset/50" : ""}`} />)}
                </div>
                <div
                  data-testid={`timeline-bar-${a.id}`}
                  onPointerDown={(e) => beginDrag(e, a, "move")}
                  className={`absolute top-2 h-7 text-[11.5px] font-semibold flex items-center overflow-hidden whitespace-nowrap rounded-[6px] cursor-grab active:cursor-grabbing select-none ${barCls}`}
                  style={barStyle(a)}
                  title={`${a.label}${a.kind === "vehicle" && a.driver_name ? ` · with ${a.driver_name}` : ""}  ${fmtDate(start_date)} – ${fmtDate(end_date)}${conflict ? "  ⚠ double-booked" : ""}${drvConflict ? "  ⚠ driver double-booked" : ""}`}
                >
                  <span onPointerDown={(e) => beginDrag(e, a, "resize-left")} className="absolute left-0 top-0 h-full w-2 cursor-ew-resize hover:bg-black/15 z-20" data-testid={`resize-left-${a.id}`} />
                  <span className="px-3 truncate pointer-events-none">{a.label}{a.kind === "vehicle" && a.driver_name ? ` · ${a.driver_name}` : ""}</span>
                  <span onPointerDown={(e) => beginDrag(e, a, "resize-right")} className="absolute right-0 top-0 h-full w-2 cursor-ew-resize hover:bg-black/15 z-20" data-testid={`resize-right-${a.id}`} />
                </div>
                {/* Stand Down segments — only over the stood-down period */}
                {a.status !== "off_hire" && sds.map((s, i) => {
                  const p = rangePct(s.stand_down_from, s.stand_down_to || s.stand_down_from);
                  if (!p) return null;
                  return <div key={s.id || i} data-testid={`standdown-seg-${a.id}`} title={`Stand Down: ${s.reason || ""} (${s.stand_down_from}${s.stand_down_to ? " – " + s.stand_down_to : ""})`}
                    className="absolute top-2 h-7 bg-[#FBE0E0] rounded-[5px] pointer-events-none z-10 border border-[#E39B9B]" style={p} />;
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------------------- Bottom panel -------------------------------- */
const DOCKET_STATUS = {
  draft: { t: "Draft", c: "bg-gray-200 text-gray-700" },
  submitted: { t: "Submitted", c: "bg-blue-100 text-blue-700" },
  foreman_approved: { t: "Foreman Approved", c: "bg-amber-100 text-amber-800" },
  office_approved: { t: "Office Approved", c: "bg-green-100 text-green-700" },
  rejected: { t: "Rejected", c: "bg-red-100 text-red-700" },
};
const DOCKET_NEXT_LABEL = { submitted: "Foreman Approve", foreman_approved: "Office Approve" };

function DocketsTable({ rows, isWriter, onAction, onOpen }) {
  const [busy, setBusy] = useState(null);
  const act = async (id, kind) => {
    setBusy(id + kind);
    try { await api.post(`/dockets/${id}/${kind}`); await onAction(); } catch { /* ignore */ }
    setBusy(null);
  };
  const sig = (s) => s ? (s.unavailable ? "flagged" : "signed") : "—";
  return (
    <table className="w-full text-[12px]">
      <thead className="sticky top-0 z-10"><tr className="bg-surface-sunken border-b border-border">
        {["Docket #", "Date", "Asset #", "Worked hrs", "Down", "Worker", "Site Rep", "Status", ""].map((l) => (
          <th key={l} className="text-left px-3 py-2.5 font-medium text-ink-muted uppercase tracking-[0.1em] text-colhead whitespace-nowrap">{l}</th>
        ))}
      </tr></thead>
      <tbody>
        {rows.length === 0 ? <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-400">No dockets submitted.</td></tr> :
          rows.map((r) => {
            const st = DOCKET_STATUS[r.status] || DOCKET_STATUS.submitted;
            const nxt = DOCKET_NEXT_LABEL[r.status];
            return (
              <tr key={r.id} onClick={() => onOpen(r)} className="border-b border-gray-100 hover:bg-equipr-yellow/10 cursor-pointer" data-testid={`docket-row-${r.id}`}>
                <td className="px-3 py-1.5 font-mono text-gray-600">{r.docket_no}</td>
                <td className="px-3 py-1.5 text-gray-700">{fmtDate(r.date)}</td>
                <td className="px-3 py-1.5 font-semibold text-equipr-black">{r.asset_no}</td>
                <td className="px-3 py-1.5 text-gray-700 font-semibold">{r.worked_hours}</td>
                <td className="px-3 py-1.5 text-gray-500">{r.down_time_hours || 0}{r.down_time_reason ? ` · ${r.down_time_reason}` : ""}</td>
                <td className="px-3 py-1.5 text-gray-600">{r.signatures?.operator?.name || "—"} <span className="text-[10px] text-gray-400">({sig(r.signatures?.operator)})</span></td>
                <td className="px-3 py-1.5 text-gray-600">{r.signatures?.site_rep?.name || "—"} <span className="text-[10px] text-gray-400">({sig(r.signatures?.site_rep)})</span></td>
                <td className="px-3 py-1.5"><span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-sm ${st.c}`}>{st.t}</span></td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  {isWriter && nxt && (
                    <>
                      <button onClick={() => act(r.id, "approve")} disabled={busy === r.id + "approve"} data-testid={`docket-approve-${r.id}`}
                        className="px-2 py-0.5 mr-1.5 text-[11px] font-bold bg-equipr-yellow text-equipr-black rounded-none hover:bg-equipr-yellow-dark disabled:opacity-50">{nxt}</button>
                      <button onClick={() => act(r.id, "reject")} disabled={busy === r.id + "reject"} data-testid={`docket-reject-${r.id}`}
                        className="px-2 py-0.5 text-[11px] font-semibold border border-red-300 text-red-600 rounded-none hover:bg-red-50">Reject</button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
      </tbody>
    </table>
  );
}

function fmtClock(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}

function BottomAttCol({ title, color, rows, empty, testid }) {
  return (
    <div className="border border-gray-200 flex flex-col min-h-0" data-testid={testid}>
      <div className={`px-3 py-1.5 bg-gray-50 border-b border-gray-200 text-[11px] font-bold uppercase tracking-wider shrink-0 ${color}`}>{title}</div>
      <div className="flex-1 overflow-auto divide-y divide-gray-100">
        {rows.length === 0 ? (
          <p className="text-xs text-gray-400 px-3 py-3">{empty}</p>
        ) : rows.map((r, i) => (
          <div key={i} className="px-3 py-1.5 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-equipr-black truncate">{r.label}</div>
              {r.sub && <div className="text-[11px] text-gray-400 truncate">{r.sub}</div>}
            </div>
            {r.time && <div className="text-[11px] text-gray-500 shrink-0">{fmtClock(r.time)}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function BottomPanel({ projectId, refreshKey, onHide }) {
  const { isWriter } = useAuth();
  const [tab, setTab] = useState("standdowns");
  const [data, setData] = useState({});
  const [openDocket, setOpenDocket] = useState(null);
  const [openDiary, setOpenDiary] = useState(null);
  const [organise, setOrganise] = useState(false);
  const [openTransport, setOpenTransport] = useState(null);
  const [height, setHeight] = useState(() => {
    const saved = Number(localStorage.getItem("eq_bottom_h"));
    return saved && saved > 100 ? saved : 190;
  });
  const dragRef = React.useRef(null);

  const onHandleDown = (e) => {
    e.preventDefault();
    dragRef.current = { startY: e.clientY, startH: height };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      let h = d.startH + (d.startY - ev.clientY);
      h = Math.max(120, Math.min(window.innerHeight - 220, h));
      setHeight(h);
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  useEffect(() => { localStorage.setItem("eq_bottom_h", String(height)); }, [height]);

  const [pstate, setPstate] = useState("loading");
  const load = useCallback(async () => {
    if (!projectId) { setData({}); setPstate("ready"); return; }
    setPstate("loading");
    const endpoints = { standdowns: "/standdowns", offhires: "/offhires", dockets: "/dockets", site_diaries: "/site-diaries", transportation: "/transportation", logs: "/logs" };
    const params = { params: { project_id: projectId } };
    const results = {};
    let failures = 0;
    await Promise.all(Object.entries(endpoints).map(async ([k, url]) => {
      try { const { data } = await api.get(url, params); results[k] = data; } catch { results[k] = []; failures++; }
    }));
    try { const { data } = await api.get(`/projects/${projectId}/attendance`); results.attendance = data; }
    catch { results.attendance = null; failures++; }
    setData(results);
    setPstate(failures >= Object.keys(endpoints).length ? "error" : "ready");
  }, [projectId, refreshKey]);
  useEffect(() => { load(); }, [load]);

  const cnt = (k) => (projectId ? (data[k] || []).length : "—");
  const TABS = [
    ["attendance", "On site", projectId ? (data.attendance ? data.attendance.on_site.length : "—") : "—"],
    ["standdowns", "Stand downs", cnt("standdowns")],
    ["offhires", "Off hires", cnt("offhires")],
    ["dockets", "Dockets", cnt("dockets")],
    ["site_diaries", "Site diary", cnt("site_diaries")],
    ["transportation", "Transportation", cnt("transportation")],
    ["logs", "Logs", cnt("logs")],
  ];

  const cols = {
    standdowns: [["asset_no", "Asset #"], ["supplier", "Supplier"], ["reason", "Reason"], ["stand_down_from", "From"], ["stand_down_to", "To"], ["stood_down_by", "By"]],
    offhires: [["asset_no", "Asset #"], ["supplier", "Supplier"], ["off_hire_date", "Date"], ["reason", "Reason"], ["recorded_by", "By"]],
    transportation: [["asset_no", "Asset #"], ["from_location", "From"], ["to_location", "To"], ["carrier", "Carrier"], ["scheduled_at", "Scheduled"], ["status", "Status"]],
    logs: [["timestamp", "Time"], ["entity", "Entity"], ["action", "Action"], ["user", "User"], ["details", "Details"]],
  };
  const rows = data[tab] || [];

  return (
    <div className="mx-4 mb-4 rounded-[12px] border border-border bg-surface-card shadow-card flex flex-col shrink-0 overflow-hidden" style={{ height }}>
      <div onPointerDown={onHandleDown} data-testid="bottom-panel-resize"
        className="h-1.5 flex items-center justify-center group shrink-0"
        style={{ cursor: "row-resize" }}>
        <div className="w-10 h-1 rounded-full bg-border group-hover:bg-equipr-yellow transition-colors" />
      </div>
      <div className="flex border-b border-border px-2 items-center bg-surface-card">
        {TABS.map(([k, l, c]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`bottom-tab-${k}`}
            className={`px-3 py-2.5 text-[12.5px] border-b-2 whitespace-nowrap ${tab === k ? "border-equipr-yellow text-ink-primary font-bold" : "border-transparent font-medium text-ink-secondary hover:text-ink-primary"}`}>
            {l}<span className="ml-1.5 font-mono text-[10px] text-ink-tertiary">{c}</span>
          </button>
        ))}
        {tab === "transportation" && isWriter && projectId && (
          <button onClick={() => setOrganise(true)} data-testid="bottom-organise-transport"
            className="ml-auto my-1 h-7 px-3 bg-equipr-yellow text-black text-[11px] font-bold uppercase tracking-[0.04em] rounded-[7px] border border-accent-edge hover:bg-equipr-yellow-dark inline-flex items-center gap-1">
            <Plus size={13} weight="bold" /> Transport
          </button>
        )}
        <button onClick={onHide} data-testid="bottom-panel-hide"
          className={`${tab === "transportation" && isWriter && projectId ? "" : "ml-auto"} my-1 h-7 px-2.5 text-[11px] font-bold uppercase tracking-[0.04em] text-ink-secondary hover:text-ink-primary inline-flex items-center gap-1`}>
          Hide <CaretDown size={12} weight="bold" />
        </button>
      </div>
      <div className="flex-1 overflow-auto">
        {!projectId ? (
          <div className="p-8 flex flex-col items-center justify-center text-center text-ink-tertiary text-[13px]" data-testid="bottom-noproject">
            <Rows size={26} className="mb-2 opacity-50" />
            Select a project on the left to load its records.
          </div>
        ) : pstate === "loading" ? (
          <div className="p-4 space-y-2" data-testid="bottom-loading">
            {[...Array(4)].map((_, i) => <div key={i} className="h-8 rounded-[6px] bg-surface-inset animate-pulse" />)}
          </div>
        ) : pstate === "error" ? (
          <div className="p-8 flex flex-col items-center justify-center text-center text-[13px]" data-testid="bottom-error">
            <span className="text-red-600 font-semibold">Couldn't load these records.</span>
            <button onClick={load} className="mt-2 h-8 px-3 border border-border-control rounded-[7px] text-[12px] font-bold uppercase tracking-[0.04em] text-ink-secondary hover:bg-surface-panel">Retry</button>
          </div>
        ) : tab === "attendance" ? (
          !data.attendance ? (
            <div className="p-6 text-center text-gray-400 text-sm">Loading…</div>
          ) : (
            <div className="grid grid-cols-3 gap-3 p-3 h-full" data-testid="bottom-attendance">
              <BottomAttCol title={`On site (${data.attendance.on_site.length})`} color="text-green-600" testid="bottom-att-onsite"
                rows={data.attendance.on_site.map((r) => ({ label: r.name, sub: [r.is_visitor ? "Visitor" : r.company, r.on_break ? "on break" : (r.break_minutes ? `${Math.round(r.break_minutes)}m break` : "")].filter(Boolean).join(" · "), time: r.sign_in_at }))} empty="Nobody signed in yet" />
              <BottomAttCol title={`Signed out (${data.attendance.signed_out.length})`} color="text-gray-500" testid="bottom-att-signedout"
                rows={data.attendance.signed_out.map((r) => ({ label: r.name, sub: [r.hours != null ? `${r.hours} h on site` : "", r.break_minutes ? `${Math.round(r.break_minutes)}m break` : ""].filter(Boolean).join(" · "), time: r.sign_out_at }))} empty="—" />
              <BottomAttCol title={`Missing (${data.attendance.missing.length})`} color="text-red-600" testid="bottom-att-missing"
                rows={data.attendance.missing.map((r) => ({ label: r.name, sub: "Expected today", time: null }))} empty="All expected workers present" />
            </div>
          )
        ) : tab === "dockets" ? (
          <DocketsTable rows={rows} isWriter={isWriter} onAction={load} onOpen={setOpenDocket} />
        ) : tab === "site_diaries" ? (
          <table className="w-full text-[12px]">
            <thead className="sticky top-0 z-10"><tr className="bg-surface-sunken border-b border-border">
              {["Date", "Status", "Signed by", "Weather (AM/PM)", "Rain", "Personnel", "Delays", "Safety"].map((l) => (
                <th key={l} className="text-left px-3 py-2.5 font-medium text-ink-muted uppercase tracking-[0.1em] text-colhead whitespace-nowrap">{l}</th>
              ))}
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">No site diary entries.</td></tr> :
                rows.map((r) => (
                  <tr key={r.id} onClick={() => setOpenDiary(r)} className="border-b border-gray-100 hover:bg-equipr-yellow/10 cursor-pointer" data-testid={`sitediary-row-${r.id}`}>
                    <td className="px-3 py-1.5 font-semibold text-equipr-black">{fmtDate(r.date)}</td>
                    <td className="px-3 py-1.5">
                      <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-sm ${r.status === "signed" ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>{r.status === "signed" ? "Signed" : "Draft"}</span>
                    </td>
                    <td className="px-3 py-1.5 text-gray-600">{r.signoff?.name || "—"}</td>
                    <td className="px-3 py-1.5 text-gray-600">{(r.weather?.am || "—")} / {(r.weather?.pm || "—")}</td>
                    <td className="px-3 py-1.5 text-gray-500">{r.weather?.rain_mm != null ? `${r.weather.rain_mm}mm` : "—"}</td>
                    <td className="px-3 py-1.5 text-gray-500">{(r.personnel || []).reduce((s, p) => s + (Number(p.count) || 0), 0)}</td>
                    <td className="px-3 py-1.5 text-gray-500">{(r.delays || []).length}</td>
                    <td className="px-3 py-1.5 text-gray-500">{(r.safety || []).length}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-[12px]">
            <thead className="sticky top-0 z-10"><tr className="bg-surface-sunken border-b border-border">{cols[tab].map(([, l]) => <th key={l} className="text-left px-3 py-2.5 font-medium text-ink-muted uppercase tracking-[0.1em] text-colhead whitespace-nowrap">{l}</th>)}</tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={cols[tab].length} className="px-3 py-10 text-center text-ink-tertiary text-[13px]" data-testid={`bottom-empty-${tab}`}>{{ standdowns: "No stand downs recorded for this project.", offhires: "No off hires recorded for this project.", transportation: "No transport movements yet — use ‘+ Transport’ to organise one.", logs: "No activity logged yet." }[tab] || "No records."}</td></tr> :
                rows.map((r, i) => {
                  const clickable = tab === "transportation";
                  return (
                  <tr key={r.id || i} onClick={clickable ? () => setOpenTransport(r) : undefined}
                    data-testid={clickable ? `transport-row-${r.id}` : undefined}
                    className={`border-b border-divider-row ${clickable ? "cursor-pointer hover:bg-accent-wash" : ""}`}>
                    {cols[tab].map(([k]) => {
                      const dk = ["stand_down_from", "stand_down_to", "off_hire_date", "date"].includes(k);
                      if (tab === "transportation" && k === "status") {
                        const TS = { scheduled: ["Scheduled", "bg-surface-inset text-ink-secondary border-border", "#8A857A"], in_progress: ["In Progress", "bg-[#FBF2CC] text-[#7A5B00] border-[#EAD79A]", "#C99A00"], delivered: ["Completed", "bg-[#E4F2E4] text-[#256428] border-[#BFE0C0]", "#2E7D32"] };
                        const s = TS[r[k]] || TS.scheduled;
                        return <td key={k} className="px-3 py-2 text-cell"><span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.04em] rounded-full border ${s[1]}`}><span className="w-1.5 h-1.5 rounded-full" style={{ background: s[2] }} />{s[0]}</span></td>;
                      }
                      const val = k === "timestamp" ? (r[k] ? fmtDateTime(r[k]) : "—") : k === "scheduled_at" ? (r[k] ? fmtDateTime(r[k]) : "—") : dk ? fmtDate(r[k]) : (r[k] || "—");
                      return <td key={k} className="px-3 py-2 text-cell text-ink-secondary">{val}</td>;
                    })}
                  </tr>
                  );
                })}
            </tbody>
          </table>
        )}
      </div>
      {openDocket && <DocketDetailModal docket={openDocket} onClose={() => setOpenDocket(null)} onChanged={load} />}
      {openDiary && <SiteDiaryModal projectId={openDiary.project_id} date={openDiary.date} projectName={openDiary.project_name} onClose={() => setOpenDiary(null)} />}
      {organise && <OrganiseTransportModal prefill={{ from_type: "project", from_id: projectId, project_id: projectId }} onClose={() => setOrganise(false)} onCreated={load} />}
      {openTransport && <TransportDetailModal transport={openTransport} onClose={() => setOpenTransport(null)} onChanged={load} />}
    </div>
  );
}

/* ---------------------- Column / Saved View control ----------------------- */
function ViewMenu({ columns, setColumns, activeViewId, setActiveViewId, views, reloadViews, defaultViewId, onSetDefault }) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const activeName = activeViewId === "default"
    ? "Default View"
    : (views.find((v) => v.id === activeViewId)?.name || "Custom");

  const toggleCol = (key) => {
    if (key === "label") return;
    setColumns((cur) => orderCols(cur.includes(key) ? cur.filter((c) => c !== key) : [...cur, key]));
  };

  const selectView = (v) => {
    if (v === "default") { setColumns(DEFAULT_COLUMNS); setActiveViewId("default"); }
    else { setColumns(orderCols(v.columns)); setActiveViewId(v.id); }
  };

  const saveNew = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/views", { name: newName.trim(), columns, shared: false });
      setNewName("");
      await reloadViews();
      setActiveViewId(data.id);
    } catch { /* ignore */ }
    setBusy(false);
  };

  const updateCurrent = async () => {
    if (activeViewId === "default") return;
    const v = views.find((x) => x.id === activeViewId);
    setBusy(true);
    try { await api.put(`/views/${activeViewId}`, { name: v.name, columns, shared: !!v.shared }); await reloadViews(); } catch { /* ignore */ }
    setBusy(false);
  };

  const toggleShare = async (v) => {
    setBusy(true);
    try { await api.put(`/views/${v.id}`, { name: v.name, columns: v.columns, shared: !v.shared }); await reloadViews(); } catch { /* ignore */ }
    setBusy(false);
  };

  const setDefault = async (id) => {
    setBusy(true);
    try { await onSetDefault(id); } catch { /* ignore */ }
    setBusy(false);
  };

  const deleteView = async (id) => {
    setBusy(true);
    try { await api.delete(`/views/${id}`); await reloadViews(); if (activeViewId === id) selectView("default"); } catch { /* ignore */ }
    setBusy(false);
  };

  const Star = ({ filled }) => <span className={filled ? "text-accent-ink" : "text-gray-300"} title="Set as my default">{filled ? "★" : "☆"}</span>;

  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} data-testid="view-menu-button"
        className="h-8 px-3 border border-gray-300 text-sm text-equipr-black hover:bg-gray-100 rounded-none inline-flex items-center gap-1.5">
        <Columns size={15} weight="bold" /> View: <span className="font-semibold">{activeName}</span> <CaretDown size={12} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-9 z-40 bg-white border border-gray-300 shadow-xl w-80 text-sm" data-testid="view-menu-panel">
            <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 text-[11px] font-bold uppercase tracking-wider text-gray-500">Saved Views</div>
            <div className="max-h-48 overflow-auto">
              <div className={`flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 ${activeViewId === "default" ? "font-bold text-equipr-black" : ""}`}>
                <button onClick={() => setDefault("default")} data-testid="view-default-default"><Star filled={defaultViewId === "default" || !defaultViewId} /></button>
                <button onClick={() => selectView("default")} data-testid="view-select-default" className="text-left flex-1 truncate">Default View</button>
                {activeViewId === "default" && <span className="text-accent-ink text-xs">active</span>}
              </div>
              {views.map((v) => (
                <div key={v.id} className={`flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 ${activeViewId === v.id ? "font-bold text-equipr-black" : ""}`}>
                  <button onClick={() => setDefault(v.id)} data-testid={`view-default-${v.id}`}><Star filled={defaultViewId === v.id} /></button>
                  <button onClick={() => selectView(v)} data-testid={`view-select-${v.id}`} className="text-left flex-1 truncate">
                    {v.name}
                    {!v.is_mine && <span className="ml-1 text-[10px] text-gray-400">· {v.owner_name}</span>}
                  </button>
                  {v.shared && <span className="text-[10px] px-1 bg-equipr-yellow/40 text-equipr-black" title="Shared with team">TEAM</span>}
                  {v.is_mine && (
                    <>
                      <button onClick={() => toggleShare(v)} data-testid={`view-share-${v.id}`} title={v.shared ? "Unshare" : "Share with team"}
                        className={`text-xs ${v.shared ? "text-accent-ink" : "text-gray-400 hover:text-equipr-black"}`}>
                        <ShareNetwork size={14} />
                      </button>
                      <button onClick={() => deleteView(v.id)} className="text-gray-400 hover:text-red-600" data-testid={`view-delete-${v.id}`}><Trash size={13} /></button>
                    </>
                  )}
                </div>
              ))}
            </div>

            <div className="px-3 py-2 border-t border-b border-gray-200 bg-gray-50 text-[11px] font-bold uppercase tracking-wider text-gray-500">Columns</div>
            <div className="max-h-56 overflow-auto p-2 grid grid-cols-1 gap-0.5">
              {ALL_COLUMN_KEYS.map((k) => (
                <label key={k} className={`flex items-center gap-2 px-1 py-1 text-[13px] ${COLUMN_CATALOG[k].locked ? "text-gray-400" : "cursor-pointer hover:bg-gray-50"}`}>
                  <input type="checkbox" checked={columns.includes(k)} disabled={COLUMN_CATALOG[k].locked}
                    onChange={() => toggleCol(k)} data-testid={`col-toggle-${k}`} />
                  {COLUMN_CATALOG[k].label}{COLUMN_CATALOG[k].locked && " (fixed)"}
                </label>
              ))}
            </div>

            <div className="px-3 py-2 border-t border-gray-200 space-y-2">
              {activeViewId !== "default" && (
                <button onClick={updateCurrent} disabled={busy} data-testid="view-update-button"
                  className="w-full h-8 border border-equipr-black text-equipr-black text-xs font-semibold rounded-none hover:bg-gray-100">
                  Update "{activeName}"
                </button>
              )}
              <div className="flex gap-2">
                <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Save as new view…" data-testid="view-name-input"
                  className="flex-1 h-8 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none" />
                <button onClick={saveNew} disabled={busy || !newName.trim()} data-testid="view-save-button"
                  className="h-8 px-3 bg-equipr-yellow text-equipr-black text-xs font-bold rounded-none hover:bg-equipr-yellow-dark disabled:opacity-50">Save</button>
              </div>
              <p className="text-[10px] text-gray-400">★ sets your default view (auto-loads on login). Share icon makes a view available to the whole team.</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* -------------------------------- Main ------------------------------------ */
export default function Scheduler() {
  const { isWriter, user, checkSession } = useAuth();
  const rateTypes = useLookup("rate_types");
  const hireTypes = useLookup("hire_types");
  const lookups = useMemo(() => ({ rate_types: rateTypes, hire_types: hireTypes }), [rateTypes, hireTypes]);
  const [projects, setProjects] = useState([]);
  const [assets, setAssets] = useState([]);
  const [operators, setOperators] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [crews, setCrews] = useState([]);
  const [selected, setSelected] = useState(null);
  const [sel, setSel] = useState(new Map());
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkTransport, setBulkTransport] = useState(false);
  const [transportPrompt, setTransportPrompt] = useState(null);
  const [bulkReallocate, setBulkReallocate] = useState(false);
  const [transportArranged, setTransportArranged] = useState(new Set());
  const bulkRehire = useCallback(async (vals) => {
    for (const it of vals) {
      try { await api.put(`/projects/${it.projectId}/assign/${it.id}`, { status: "active" }); } catch { /* keep going */ }
    }
  }, []);
  const toggleSelect = useCallback((a, projectId) => {
    setSel((prev) => {
      const n = new Map(prev);
      if (n.has(a.id)) n.delete(a.id);
      else n.set(a.id, { id: a.id, projectId, label: a.label, supplier: a.supplier, kind: a.kind, ref_id: a.ref_id, status: a.status });
      return n;
    });
  }, []);
  const toggleMany = useCallback((items, projectId, checked) => {
    setSel((prev) => {
      const n = new Map(prev);
      items.forEach((a) => { if (checked) n.set(a.id, { id: a.id, projectId, label: a.label, supplier: a.supplier, kind: a.kind, ref_id: a.ref_id, status: a.status }); else n.delete(a.id); });
      return n;
    });
  }, []);
  const clearSel = useCallback(() => setSel(new Map()), []);
  const [view, setView] = useState("list");
  const [collapsedIds, setCollapsedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("eq_collapsed_projects") || "[]")); } catch { return new Set(); }
  });
  const persistCollapsed = (s) => { localStorage.setItem("eq_collapsed_projects", JSON.stringify([...s])); return s; };
  const toggleCollapse = (id) => setCollapsedIds((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return persistCollapsed(n);
  });
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [activeDrag, setActiveDrag] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // Multi-select for drag-to-assign (right rail)
  const [selKind, setSelKind] = useState(null);
  const [selIds, setSelIds] = useState([]);
  const lastClickRef = useRef(null);
  const clearSelection = useCallback(() => { setSelIds([]); setSelKind(null); lastClickRef.current = null; }, []);
  const onCardClick = useCallback((item, kind, e, orderedIds) => {
    const toggle = e.metaKey || e.ctrlKey;
    const range = e.shiftKey;
    if (kind !== selKind) {
      setSelKind(kind); setSelIds([item.id]); lastClickRef.current = item.id; return;
    }
    if (range && lastClickRef.current && orderedIds) {
      const a = orderedIds.indexOf(lastClickRef.current);
      const b = orderedIds.indexOf(item.id);
      if (a >= 0 && b >= 0) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        const rangeIds = orderedIds.slice(lo, hi + 1);
        setSelIds((cur) => Array.from(new Set([...cur, ...rangeIds])));
        return;
      }
    }
    if (toggle) {
      setSelIds((cur) => cur.includes(item.id) ? cur.filter((x) => x !== item.id) : [...cur, item.id]);
      lastClickRef.current = item.id; return;
    }
    setSelIds([item.id]); lastClickRef.current = item.id;
  }, [selKind]);
  const selectionValue = useMemo(
    () => ({ selectedIds: selIds, selectedKind: selKind, onCardClick, clear: clearSelection }),
    [selIds, selKind, onCardClick, clearSelection]);
  const [openAllocation, setOpenAllocation] = useState(null);
  const rowDrawerValue = useMemo(() => ({ open: (alloc, project) => setOpenAllocation({ alloc, project }) }), []);

  // Saved list views / custom columns
  const [views, setViews] = useState([]);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [activeViewId, setActiveViewId] = useState("default");
  const [appliedDefault, setAppliedDefault] = useState(false);
  const defaultViewId = user?.default_view_id || "default";

  // Central search & sorting
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [listQ, setListQ] = useState("");
  const filterActive = !!listQ.trim();
  const onSort = (k) => setSort((s) => s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" });

  // Density (row height) — persisted
  const DENSITIES = [["compact", 34, "py-1"], ["default", 42, "py-2"], ["comfortable", 52, "py-3"]];
  const [density, setDensity] = useState(() => Number(localStorage.getItem("eq_density")) || 42);
  const densRec = DENSITIES.find((d) => d[1] === density) || DENSITIES[1];
  const rowPad = densRec[2];
  const cycleDensity = () => setDensity((cur) => {
    const idx = DENSITIES.findIndex((d) => d[1] === cur);
    const next = DENSITIES[(idx + 1) % DENSITIES.length][1];
    localStorage.setItem("eq_density", String(next));
    return next;
  });

  // Right rail & bottom panel visibility
  const [railOpen, setRailOpen] = useState(() => localStorage.getItem("eq_rail_open") !== "0");
  const toggleRail = (v) => { localStorage.setItem("eq_rail_open", v ? "1" : "0"); setRailOpen(v); };
  const [panelHidden, setPanelHidden] = useState(false);

  const loadViews = useCallback(async () => {
    try { const { data } = await api.get("/views"); setViews(data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadViews(); }, [loadViews]);

  // apply the user's default view once on load
  useEffect(() => {
    if (appliedDefault || !user) return;
    if (defaultViewId === "default") { setAppliedDefault(true); return; }
    const v = views.find((x) => x.id === defaultViewId);
    if (v) { setColumns(orderCols(v.columns)); setActiveViewId(v.id); setAppliedDefault(true); }
    else if (views.length || defaultViewId === "default") { setAppliedDefault(true); }
  }, [views, user, defaultViewId, appliedDefault]);

  const setDefaultView = useCallback(async (id) => {
    if (id === "default") await api.delete("/views/default/clear");
    else await api.put(`/views/${id}/default`);
    await checkSession();
  }, [checkSession]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = useCallback(async () => {
    try {
      const [p, a, o, t, v, c] = await Promise.all([api.get("/projects"), api.get("/assets"), api.get("/operators"), api.get("/transportation"), api.get("/vehicles"), api.get("/crews")]);
      const inTransit = new Set();
      (t.data || []).filter((x) => x.status === "in_transit").forEach((x) => {
        (x.asset_nos && x.asset_nos.length ? x.asset_nos : [x.asset_no]).forEach((an) => an && inTransit.add(an));
      });
      const arranged = new Set();
      (t.data || []).filter((x) => x.status === "scheduled" || x.status === "in_transit").forEach((x) => {
        (x.asset_nos && x.asset_nos.length ? x.asset_nos : [x.asset_no]).forEach((an) => an && arranged.add(an));
      });
      setTransportArranged(arranged);
      setProjects(p.data);
      setAssets(a.data.map((x) => ({ ...x, _in_transit: inTransit.has(x.asset_no) })));
      setOperators(o.data.filter((x) => x.employment_status !== "inactive"));
      setVehicles(v.data);
      setCrews(c.data);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); setRefreshKey((k) => k + 1); };

  const onDragEnd = async ({ active, over }) => {
    setActiveDrag(null);
    if (!over || !active?.data?.current) return;
    const projectId = over.data?.current?.projectId;
    if (!projectId) return;
    const { kind, item } = active.data.current;
    if (kind === "crew") {
      const proj = projects.find((p) => p.id === projectId);
      const alreadyOn = new Set((proj?.assignments || [])
        .filter((a) => a.kind === "operator" && a.status !== "off_hire" && a.status !== "demobilised").map((a) => a.ref_id));
      const members = (item.member_ids || []).filter((id) => operators.some((o) => o.id === id) && !alreadyOn.has(id));
      if (members.length === 0) { alert("Everyone in this crew is already on that project (or inactive)."); return; }
      const expiredMsgs = members.map((id) => operators.find((o) => o.id === id))
        .filter(Boolean)
        .map((o) => ({ o, exp: complianceStatus(o).expired }))
        .filter((x) => x.exp.length)
        .map((x) => `${x.o.name}: ${x.exp.map((c) => c.name).join(", ")}`);
      if (expiredMsgs.length && !window.confirm(`⚠ Expired compliance in this crew:\n${expiredMsgs.join("\n")}\n\nAllocate anyway?`)) return;
      try {
        for (const mid of members) {
          await api.post(`/projects/${projectId}/assign`, { kind: "operator", ref_id: mid });
        }
        refresh();
      } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail)); }
      return;
    }
    let items = [item];
    if (selKind === kind && selIds.includes(item.id) && selIds.length > 1) {
      const src = kind === "asset" ? assets : kind === "vehicle" ? vehicles : operators;
      items = selIds.map((id) => src.find((x) => x.id === id)).filter(Boolean);
      if (!items.some((x) => x.id === item.id)) items.push(item);
    }
    if (kind === "operator") {
      const expiredMsgs = items.map((o) => ({ o, exp: complianceStatus(o).expired }))
        .filter((x) => x.exp.length)
        .map((x) => `${x.o.name}: ${x.exp.map((c) => c.name).join(", ")}`);
      if (expiredMsgs.length && !window.confirm(`⚠ Expired compliance:\n${expiredMsgs.join("\n")}\n\nAllocate anyway?`)) return;
    }
    try {
      for (const it of items) {
        await api.post(`/projects/${projectId}/assign`, { kind, ref_id: it.id });
      }
      clearSelection();
      refresh();
      if (kind === "asset") {
        const labels = items.map((x) => x.asset_no).filter(Boolean);
        const proj = projects.find((p) => p.id === projectId);
        const pname = proj ? (proj.project_name || proj.site_address || "this project") : "this project";
        if (labels.length && window.confirm(`Set up transport for ${labels.length === 1 ? labels[0] : `${labels.length} assets`} to ${pname}?`)) {
          setTransportPrompt({ asset_nos: labels, from_type: "site", from_id: "", to_type: "project", to_id: projectId });
        }
      }
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // sidebar list grouped by client
  const filteredProjects = projects.filter((p) => !q || JSON.stringify(p).toLowerCase().includes(q.toLowerCase()));
  const byClient = useMemo(() => {
    const g = {};
    filteredProjects.forEach((p) => { (g[p.client || "—"] = g[p.client || "—"] || []).push(p); });
    return g;
  }, [filteredProjects]);
  const allocCount = (p) => (p.assignments || []).length;

  const centerProjects = selected ? projects.filter((p) => p.id === selected) : projects;
  const selectedProject = selected ? projects.find((p) => p.id === selected) : null;
  const selectedProjectName = selectedProject ? (selectedProject.project_name || selectedProject.site_address) : null;
  const totalAlloc = centerProjects.reduce((s, p) => s + allocCount(p), 0);
  const sdCount = centerProjects.reduce((s, p) => s + (p.assignments || []).filter((a) => a.status === "stand_down").length, 0);
  const ohCount = centerProjects.reduce((s, p) => s + (p.assignments || []).filter((a) => a.status === "off_hire").length, 0);
  const shownAlloc = listQ.trim()
    ? centerProjects.reduce((s, p) => s + applyFilterSort(p.assignments || [], listQ, { key: null }).length, 0)
    : totalAlloc;

  return (
    <SelectionCtx.Provider value={selectionValue}>
    <RowDrawerCtx.Provider value={rowDrawerValue}>
    <DndContext sensors={sensors} onDragStart={({ active }) => setActiveDrag(active.data.current)} onDragEnd={onDragEnd}>
      <div className="h-full flex overflow-hidden">
        {/* Projects panel */}
        <aside className="w-[260px] bg-surface-card border-r border-border flex flex-col shrink-0">
          <div className="px-3 py-3 border-b border-divider-inner">
            <div className="flex items-center justify-between mb-2.5">
              <h2 className="font-heading text-[15px] font-bold text-ink-primary">Projects</h2>
              {isWriter && <button onClick={() => { setEditProject(null); setDialogOpen(true); }} data-testid="new-project-sidebar" title="New project" className="w-7 h-7 flex items-center justify-center rounded-[7px] border border-border-control text-ink-primary hover:bg-surface-panel"><Plus size={16} weight="bold" /></button>}
            </div>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Project, site or ref" data-testid="project-search"
                className="w-full h-9 pl-8 pr-2 border border-border-control bg-white text-[13px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto py-1">
            <button onClick={() => setSelected(null)} data-testid="show-all-projects"
              className={`w-full flex items-center justify-between px-3 py-2 text-[13px] border-l-2 ${!selected ? "border-accent-edge bg-accent-tint font-semibold text-ink-primary" : "border-transparent text-ink-secondary hover:bg-surface-panel"}`}>
              <span>All Projects</span>
              <span className="font-mono text-mono-badge text-ink-tertiary">{projects.length}</span>
            </button>
            {Object.entries(byClient).map(([client, items]) => (
              <div key={client} className="mt-1">
                <div className="px-3 pt-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.14em] text-ink-muted truncate">{client}</div>
                {items.map((p) => (
                  <button key={p.id} onClick={() => setSelected(p.id)} data-testid={`sidebar-project-${p.id}`}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-[13px] border-l-2 ${selected === p.id ? "border-accent-edge bg-accent-tint font-semibold text-ink-primary" : "border-transparent text-ink-secondary hover:bg-surface-panel"}`}>
                    <span className="truncate">{p.site_address}</span>
                    <span className="shrink-0 font-mono text-mono-badge text-ink-tertiary">{allocCount(p)}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </aside>

        {/* Center */}
        <section className="flex-1 flex flex-col min-w-0 bg-surface-page">
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border bg-surface-card no-print">
            <div className="flex items-center bg-surface-inset rounded-[999px] p-0.5 shrink-0">
              {[["list", "List", Rows], ["timeline", "Timeline", CalendarBlank], ["dashboard", "Dashboard", ChartBar], ["weekly", "Weekly", FileText]].map(([k, l, Icon]) => (
                <button key={k} onClick={() => setView(k)} data-testid={`view-${k}`}
                  className={`flex items-center gap-1.5 px-3 h-8 rounded-[999px] text-[12px] font-bold uppercase tracking-[0.04em] transition-colors ${view === k ? "bg-equipr-yellow text-black" : "text-ink-secondary hover:text-ink-primary"}`}>
                  <Icon size={14} weight="bold" /> {l}
                </button>
              ))}
            </div>
            {view === "list" ? (
              <div className="relative flex-1 min-w-[280px] max-w-[520px]">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                <input value={listQ} onChange={(e) => setListQ(e.target.value)} data-testid="list-search"
                  placeholder="Asset #, worker, supplier or project ref"
                  className="w-full h-[42px] pl-10 pr-14 bg-white border-[1.5px] border-ink-primary text-[13px] rounded-[9px] focus:ring-2 focus:ring-equipr-yellow/35 focus:outline-none" />
                <span data-testid="list-search-count" className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-[10.5px] text-ink-tertiary">
                  {listQ.trim() ? `${shownAlloc} of ${totalAlloc}` : totalAlloc}
                </span>
              </div>
            ) : <div className="flex-1" />}
            {isWriter && (
              <button onClick={() => { setEditProject(null); setDialogOpen(true); }} data-testid="new-project-button"
                className="shrink-0 h-[42px] px-4 bg-equipr-yellow text-black font-bold text-[12px] uppercase tracking-[0.04em] rounded-[9px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px inline-flex items-center gap-1.5">
                <Plus size={15} weight="bold" /> New Project
              </button>
            )}
          </div>

          {error && <div className="m-3 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}

          {(view === "list" || view === "timeline") ? (
            <div className="flex-1 min-h-0 p-4">
              {(!loading && centerProjects.length === 0) ? (
                <div className="h-full flex flex-col items-center justify-center text-ink-tertiary">
                  <CalendarBlank size={40} />
                  <p className="mt-2 text-[15px] font-semibold">No projects yet. Create one to start allocating.</p>
                </div>
              ) : (
                <div className="h-full flex flex-col bg-surface-card border border-border rounded-[12px] overflow-hidden shadow-card">
                  <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-divider-inner shrink-0 no-print">
                    <div className="min-w-0 flex items-center gap-2">
                      <span className="font-heading text-[15px] font-bold text-ink-primary truncate" data-testid="register-title">{selected ? selectedProjectName : "All projects"}</span>
                      {selected ? (
                        <>
                          {selectedProject?.project_no && <span className="font-mono text-mono-id text-ink-tertiary shrink-0">#{selectedProject.project_no}</span>}
                          {selectedProject?.client && <span className="text-meta text-ink-tertiary truncate">· {selectedProject.client}</span>}
                        </>
                      ) : (
                        <span className="text-meta text-ink-tertiary">· {centerProjects.length} project{centerProjects.length === 1 ? "" : "s"}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => {
                          const allCollapsed = centerProjects.every((p) => collapsedIds.has(p.id));
                          setCollapsedIds(persistCollapsed(allCollapsed ? new Set() : new Set(centerProjects.map((p) => p.id))));
                        }}
                        data-testid="collapse-all-btn"
                        className="h-8 px-2.5 border border-border-control text-ink-secondary hover:bg-surface-panel rounded-[7px] inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.04em]">
                        {centerProjects.every((p) => collapsedIds.has(p.id))
                          ? <><ArrowsOutLineVertical size={14} /> Expand all</>
                          : <><ArrowsInLineVertical size={14} /> Collapse all</>}
                      </button>
                      <ViewMenu columns={columns} setColumns={setColumns} activeViewId={activeViewId}
                        setActiveViewId={setActiveViewId} views={views} reloadViews={loadViews}
                        defaultViewId={defaultViewId} onSetDefault={setDefaultView} />
                      <button onClick={refresh} data-testid="refresh-button" title="Refresh" className="h-8 w-8 flex items-center justify-center border border-border-control text-ink-secondary hover:bg-surface-panel rounded-[7px]"><ArrowsClockwise size={15} /></button>
                      {!railOpen && (
                        <button onClick={() => toggleRail(true)} data-testid="rail-reopen" title="Show resources" className="h-8 w-8 flex items-center justify-center border border-border-control text-ink-secondary hover:bg-surface-panel rounded-[7px]"><Columns size={15} /></button>
                      )}
                    </div>
                  </div>
                  <div className="flex-1 overflow-hidden">
                    {view === "list" ? (
                      <div className="h-full overflow-y-auto">
                        <ListHeader columns={columns} sort={sort} onSort={onSort} />
                        {loading ? (
                          <table className="w-full text-[13px] table-fixed" data-testid="scheduler-list-skeleton">
                            <ColGroup columns={columns} />
                            <tbody><SkeletonRows cols={columns.length + 1} rows={10} /></tbody>
                          </table>
                        ) : centerProjects.map((p) => (
                          <ProjectGroup key={p.id} project={p} onChange={refresh} selected={selected === p.id}
                            onSelect={setSelected} onEdit={(pr) => { setEditProject(pr); setDialogOpen(true); }}
                            columns={columns} lookups={lookups} listQ={listQ} sort={sort} filterActive={filterActive}
                            collapsed={collapsedIds.has(p.id)} onToggleCollapse={toggleCollapse} rowPad={rowPad}
                            sel={sel} onToggleSelect={toggleSelect} onToggleMany={toggleMany} />
                        ))}
                      </div>
                    ) : (
                      <TimelineView projects={centerProjects} onChange={refresh} />
                    )}
                  </div>
                  <div className="flex items-center justify-between px-4 py-2 border-t border-divider-inner bg-surface-panel text-meta text-ink-tertiary shrink-0 no-print">
                    <span data-testid="list-footer">{totalAlloc} allocations · {sdCount} stand downs · {ohCount} off hires</span>
                    <button onClick={cycleDensity} data-testid="density-toggle" className="font-mono text-[10.5px] hover:text-ink-primary">
                      Density {density}px · saved to your profile
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4">
              {view === "dashboard"
                ? <DashboardView projectId={selected} projectName={selectedProjectName} />
                : <WeeklySummaryView projectId={selected} />}
            </div>
          )}

          {view === "list" && sel.size > 0 && (() => {
            const vals = [...sel.values()];
            const hasAsset = vals.some((v) => v.kind === "asset");
            const hasWorker = vals.some((v) => v.kind === "operator");
            const assetsOnly = hasAsset && !hasWorker;
            const workersOnly = hasWorker && !hasAsset;
            const allEnded = vals.every((v) => v.status === "off_hire" || v.status === "demobilised");
            const anyArranged = vals.some((v) => transportArranged.has(v.label));
            const btnYellow = "h-8 px-3 bg-equipr-yellow text-black text-[12px] font-bold uppercase tracking-[0.04em] rounded-[7px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px";
            const btnWhite = "h-8 px-3 bg-white text-equipr-black text-[12px] font-bold uppercase tracking-[0.04em] rounded-[7px] hover:bg-surface-panel active:translate-y-px";
            return (
              <div className="fixed left-1/2 -translate-x-1/2 bottom-6 z-40 flex items-center gap-2 bg-equipr-black text-white rounded-[12px] shadow-drawer pl-4 pr-3 py-2 no-print" data-testid="bulk-action-bar">
                <span className="text-[13px] font-bold">{sel.size} selected</span>
                <span className="w-px h-5 bg-white/20 mx-1" />
                {assetsOnly && allEnded ? (
                  <>
                    <button onClick={() => bulkRehire(vals).then(() => { clearSel(); refresh(); })} data-testid="bulk-rehire-btn"
                      className={btnYellow}>Re-Hire</button>
                    <button onClick={() => setBulkReallocate(true)} data-testid="bulk-reallocate-btn"
                      className={`${btnWhite} inline-flex items-center gap-1.5`}><ArrowsClockwise size={14} weight="bold" /> Reallocate</button>
                    {!anyArranged && (
                      <button onClick={() => setBulkTransport(true)} data-testid="bulk-transport-btn"
                        className={`${btnWhite} inline-flex items-center gap-1.5`}><Truck size={14} weight="bold" /> Transport</button>
                    )}
                  </>
                ) : (
                  <>
                    {assetsOnly && (
                      <button onClick={() => setBulkAction("off_hire")} data-testid="bulk-offhire-btn"
                        className={btnYellow}>Off Hire</button>
                    )}
                    {workersOnly && (
                      <button onClick={() => setBulkAction("demobilise")} data-testid="bulk-demobilise-btn"
                        className={btnYellow}>Demobilise</button>
                    )}
                    <button onClick={() => setBulkAction("stand_down")} data-testid="bulk-standdown-btn"
                      className={btnWhite}>Stand Down</button>
                    {assetsOnly && (
                      <button onClick={() => setBulkReallocate(true)} data-testid="bulk-reallocate-btn"
                        className={`${btnWhite} inline-flex items-center gap-1.5`}><ArrowsClockwise size={14} weight="bold" /> Reallocate</button>
                    )}
                    {assetsOnly && !anyArranged && (
                      <button onClick={() => setBulkTransport(true)} data-testid="bulk-transport-btn"
                        className={`${btnWhite} inline-flex items-center gap-1.5`}><Truck size={14} weight="bold" /> Transport</button>
                    )}
                  </>
                )}
                <button onClick={clearSel} data-testid="bulk-clear-btn" className="h-8 px-2 text-[12px] text-white/70 hover:text-white">Clear</button>
              </div>
            );
          })()}
          {bulkAction && (
            <BulkActionModal action={bulkAction} items={[...sel.values()]}
              onClose={() => setBulkAction(null)}
              onDone={() => { setBulkAction(null); clearSel(); refresh(); }} />
          )}
          {bulkReallocate && (
            <BulkReallocateModal items={[...sel.values()]}
              onClose={() => setBulkReallocate(false)}
              onDone={() => { setBulkReallocate(false); clearSel(); refresh(); }} />
          )}
          {bulkTransport && (
            <BulkTransportModal items={[...sel.values()]}
              onClose={() => setBulkTransport(false)}
              onDone={() => { setBulkTransport(false); clearSel(); refresh(); }} />
          )}
          {transportPrompt && (
            <OrganiseTransportModal prefill={transportPrompt}
              onClose={() => setTransportPrompt(null)}
              onCreated={() => { setTransportPrompt(null); refresh(); }} />
          )}

          {view !== "dashboard" && view !== "weekly" && (
            panelHidden ? (
              <div className="mx-4 mb-4 flex items-center justify-between px-4 py-2.5 rounded-[12px] border border-border bg-surface-card shadow-card no-print">
                <span className="text-meta text-ink-tertiary">Records panel hidden</span>
                <button onClick={() => setPanelHidden(false)} data-testid="bottom-panel-show" className="text-[11px] font-bold uppercase tracking-[0.04em] text-accent-ink hover:underline inline-flex items-center gap-1"><CaretDown size={12} weight="bold" className="rotate-180" /> Show</button>
              </div>
            ) : (
              <BottomPanel projectId={selected} refreshKey={refreshKey} onHide={() => setPanelHidden(true)} />
            )
          )}
        </section>

        {/* Right rail */}
        {railOpen && <RightRail assets={assets} operators={operators} vehicles={vehicles} crews={crews} onClose={() => toggleRail(false)} onRefresh={refresh} />}
      </div>

      <DragOverlay>
        {activeDrag ? (
          <div className="px-3 py-1.5 bg-white border border-equipr-yellow shadow-lg text-[13px] font-semibold text-equipr-black inline-flex items-center">
            {activeDrag.kind === "crew" ? (<><UsersThree size={14} weight="bold" className="mr-1 text-accent-ink" />{activeDrag.item.name}<span className="ml-2 bg-equipr-black text-white text-[11px] px-1.5 py-0.5 rounded-sm">{(activeDrag.item.member_ids || []).length} workers</span></>)
              : activeDrag.kind === "asset" ? activeDrag.item.asset_no : activeDrag.kind === "vehicle" ? activeDrag.item.rego : activeDrag.item.name}
            {selKind === activeDrag.kind && selIds.includes(activeDrag.item.id) && selIds.length > 1 && (
              <span className="ml-2 bg-equipr-black text-white text-[11px] px-1.5 py-0.5 rounded-sm" data-testid="drag-overlay-count">+{selIds.length - 1} more</span>
            )}
          </div>
        ) : null}
      </DragOverlay>

      <ProjectDetailsDialog open={dialogOpen} project={editProject} onClose={() => setDialogOpen(false)} onSaved={refresh} />
      {openAllocation && <AllocationDrawer alloc={openAllocation.alloc} project={openAllocation.project} onClose={() => setOpenAllocation(null)} onChanged={refresh} />}
    </DndContext>
    </RowDrawerCtx.Provider>
    </SelectionCtx.Provider>
  );
}
